import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { floatingButtonStyle } from 'components/atoms/FloatingButton'
import { useFloatingSlot, FLOATING_PRIORITY } from 'components/atoms/floatingStack'
import styled, { keyframes } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { pathOr } from 'ramda'
import FileCategories from 'features/bpDetails/Components/files/FileCategories'
import ProcessingFilesPill from 'features/bpDetails/Components/files/ProcessingFilesPill'
import StreamlinedFilesSearch from 'features/bpDetails/Components/StreamlinedFilesSearch'
import { canEditBp } from 'utils/user'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import {
  getSelectedBp,
  selectBulkShareStatus,
  localSearchFileResults
} from 'features/bpDetails/ducks/selectors'
import { getFileTypes, selectBpFilesList, selectLoadingProgress } from 'ducks/files/selectors'
import {
  selectFileCategories,
  selectFolders
} from 'ducks/dictionaries/selectors'
import { Chip, Tab, Tabs, Tooltip, CircularProgress } from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ArticleIcon from '@mui/icons-material/Article'
import BadgeIcon from '@mui/icons-material/Badge'
import FolderIcon from '@mui/icons-material/Folder'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import CloseIcon from '@mui/icons-material/Close'
import UploadIcon from '@mui/icons-material/Upload' // Added import for the icon
import {
  bulkShareFilesRoutine,
  fetchSharedUsersForFilesRoutine,
  getBpConnectedUsersRoutine,
  fetchBpRoutine
} from 'features/bpDetails/ducks/actions'
import SearchableSelect from 'components/SearchableSelect'
import {
  getBpFilesRoutine,
  loadFilesByFolderRoutine,
  loadFilesByCategoryRoutine,
  loadAllFilesRoutine,
  getBpFilesCountsRoutine,
  invalidateCustomFolderCacheRoutine,
  cancelAutoViewLoadingRoutine,
  clearBpFilesRoutine
} from 'ducks/files/actions'
import {
  loadPortfolioFilesByFolderRoutine,
  loadPortfolioFilesByCategoryRoutine
} from 'ducks/portfolioDocuments/actions'
import { selectPortfolioCursor } from 'ducks/portfolioDocuments/selectors'
import {
  loadEstateFilesByFolderRoutine,
  loadEstateFilesByCategoryRoutine
} from 'ducks/estateDocuments/actions'
import { selectEstateCursor } from 'ducks/estateDocuments/selectors'
import { getPortfolioFileCounts } from 'services/portfolioDocumentsService'
import { getEstateFileCounts } from 'services/estateDocumentsService'
import LogoShort from 'assets/images/bp-logo-orange.png'
import ShareBulkFilesModal from './ShareBulkFilesModal'
import UnsharedBulkFilesModal from './UnshareBulkFilesModal'
import UnshareAllForEmailModal from './UnshareAllForEmailModal'
import { toast } from 'react-toastify'
import BulkEditFileModal from './EditBulkFilesModal'
import AddMultipleFilesModal from 'features/bpDetails/Components/files/AddMultipleFilesModal'
import CustomViews from 'features/bpDetails/Components/CustomViews'
import FileAnalysisPanel from 'features/bpDetails/Components/files/FileAnalysisPanel'
import FileToUnitsWizard from 'features/bpDetails/Components/files/FileToUnitsWizard'
import { getFileAnalysisSummary } from 'services/fileService'
import FileLoadingProgress from 'features/bpDetails/Components/files/FileLoadingProgress'
import TroubleshootRoundedIcon from '@mui/icons-material/TroubleshootRounded'
import CookieService from 'services/CookieService'
import useHideDuplicates from 'hooks/useHideDuplicates'
import HideDuplicatesControl from 'components/atoms/HideDuplicatesControl'
import ReclassifyFileModal from './ReclassifyFileModal'
import EditFileModal from './EditFileModal'
import FolderPermissionsIndicator from './files/FolderPermissionsIndicator'
import FileInfoPanel from './files/FileInfoPanel'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import FindInPageIcon from '@mui/icons-material/FindInPage'
import Modal from 'components/atoms/Modal'
import CheckFilesModal from './files/CheckFilesModal'
import APIService from 'services/APIService'
import { useFilesRefresh } from 'utils/uploadEvents'
import { documentCountSummary } from 'utils/documentCounts'
import {
  IDENTITY_FOLDER,
  IDENTITY_FOLDER_ID,
  IdentityAccessNote,
  IdentityFolderIcon,
  IDENTITY_SEARCH_HINT,
  IdentityLockedPanel,
  useIdentityDocumentsFolder
} from './files/IdentityDocumentsFolder'

const api = APIService()

const ClickableChip = styled(Chip)`
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary}10;
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`

const ToastWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 360px;
`

const ToastTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
`

const ToastText = styled.div`
  font-size: 13px;
  line-height: 1.4;
`

const ToastActions = styled.div`
  margin-top: 4px;
  display: flex;
  justify-content: flex-end;
`

const ToastButton = styled.button`
  border: none;
  border-radius: 12px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  background-color: #ffffff;
  color: #d24724;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
  transition: all 0.15s ease;

  &:hover {
    background-color: #ffe5dd;
  }
`

const ShortAlreadySharedToast = ({ count, onViewDetails }) => (
  <ToastWrapper>
    <ToastTitle>
      {count} file{count !== 1 ? 's' : ''} were skipped (already shared)
    </ToastTitle>
    <ToastText>
      We didn&apos;t change sharing for these files because the selected recipients already have access.
    </ToastText>
    <ToastActions>
      <ToastButton type='button' onClick={onViewDetails}>
        View details
      </ToastButton>
    </ToastActions>
  </ToastWrapper>
)

const DialogWrapper = styled.div`
  max-height: 420px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const DialogIntro = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
`

const DialogListWrapper = styled.div`
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  padding: 8px 10px;
  max-height: 260px;
  overflow-y: auto;
  background: #fff7f5;
`

const DialogList = styled.ul`
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.45;
`

const DialogListItem = styled.li`
  margin-bottom: 2px;
`

const DialogFooterNote = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black};
  margin-top: 4px;
`

const AlreadySharedDialogContent = ({ files }) => {
  if (!files || files.length === 0) return null

  return (
    <DialogWrapper>
      <DialogIntro>
        These files were not updated because the selected recipients already have access to them.
      </DialogIntro>

      <DialogListWrapper>
        <DialogList>
          {files.map((name, idx) => (
            <DialogListItem key={`${name}-${idx}`}>{name}</DialogListItem>
          ))}
        </DialogList>
      </DialogListWrapper>
    </DialogWrapper>
  )
}

// When search is active, Bedrock KB has already filtered everything
// This is just a compatibility shim - always returns true since files are pre-filtered
const matchesSearchCriteria = (file, criteria) => {
  return true
}

const applyClientSearch = (files, criteria) => {
  if (!files) return files
  const q = (criteria?.query || '').trim().toLowerCase()
  const hasTextQuery = q.length >= 2
  const hasUploaderFilter = criteria?.uploaderIds?.length > 0
  const hasTypeFilter = criteria?.fileTypeIds?.length > 0
  const hasTagFilter = criteria?.tagIds?.length > 0
  if (!hasTextQuery && !hasUploaderFilter && !hasTypeFilter && !hasTagFilter) return files
  return files.filter(file => {
    if (hasTextQuery) {
      const hay = `${file.displayName || ''} ${file.description || ''} ${file.searchableContent || ''}`.toLowerCase()
      if (!hay.includes(q)) return false
    }
    if (hasUploaderFilter && !criteria.uploaderIds.includes(file.uploader?.id)) return false
    if (hasTypeFilter) {
      const idForCompare = file.fileType?.id ?? 'null'
      if (!criteria.fileTypeIds.includes(idForCompare)) return false
    }
    if (hasTagFilter) {
      const fileTagIds = (file.taggings || []).map(t => t.tagId || t.tag?.id || t.id).filter(Boolean)
      if (!criteria.tagIds.every(tid => fileTagIds.includes(tid))) return false
    }
    return true
  })
}

const applyAggregateClientFilters = (files, criteria) => {
  if (!files) return files
  const hasUploaderFilter = criteria?.uploaderIds?.length > 0
  const hasTypeFilter = criteria?.fileTypeIds?.length > 0
  const hasTagFilter = criteria?.tagIds?.length > 0
  if (!hasUploaderFilter && !hasTypeFilter && !hasTagFilter) return files
  return files.filter(file => {
    if (hasUploaderFilter && !criteria.uploaderIds.includes(file.uploader?.id)) return false
    if (hasTypeFilter) {
      const idForCompare = file.fileType?.id ?? 'null'
      if (!criteria.fileTypeIds.includes(idForCompare)) return false
    }
    if (hasTagFilter) {
      const fileTagIds = (file.taggings || []).map(t => t.tagId || t.tag?.id || t.id).filter(Boolean)
      if (!criteria.tagIds.every(tid => fileTagIds.includes(tid))) return false
    }
    return true
  })
}

const UnifiedFiles = (props = {}) => {
  const bpFilesFromStore = useSelector(selectBpFilesList)
  const allFiles = props.filesSource ?? bpFilesFromStore
  const searchResults = useSelector(localSearchFileResults)
  const fileTypes = useSelector(getFileTypes)
  const fileCategories = useSelector(selectFileCategories)
  const folders = useSelector(selectFolders)
  const selectedBpFromStore = useSelector(getSelectedBp)
  const bp = props.bp ?? selectedBpFromStore
  const stateKeyId = props.stateKeyId ?? bp?.id
  const context = props.context ?? 'bp'
  const isAggregate = context === 'estate-aggregate' || context === 'portfolio-aggregate'
  const isPortfolioAggregate = context === 'portfolio-aggregate'
  const isEstateAggregate = context === 'estate-aggregate'
  const aggregateEntityId = isAggregate && stateKeyId ? stateKeyId.slice(stateKeyId.indexOf(':') + 1) : null
  const showSecondaryTabs = context === 'bp'
  const showCustomTab = props.showCustomTab ?? showSecondaryTabs
  const customViewContent = props.customViewContent ?? null
  const customBuildings = props.customBuildings ?? null
  const automaticTabContent = props.automaticTabContent ?? null
  const hideTabBar = Boolean(automaticTabContent) && !showCustomTab && !showSecondaryTabs
  const bpUnits = useSelector(state => state.bpDetails.units || [])
  const loadedFoldersFromStore = useSelector(
    state => state.files.loadedFolders || new Set()
  )
  const loadedFolders = props.loadedFolders ?? loadedFoldersFromStore
  const loadedCategories = useSelector(
    state => state.files.loadedCategories || new Set()
  )
  const allFilesLoadedFromStore = useSelector(
    state => state.files.allFilesLoaded || false
  )
  const allFilesLoaded = props.allFilesLoaded ?? allFilesLoadedFromStore
  const filesCountsFromStore = useSelector(state => state.files.filesCounts)
  const filesCounts = props.countsSource ?? filesCountsFromStore
  const loadingFoldersFromStore = useSelector(
    state => state.files.loadingFolders || new Set()
  )
  const loadingFolders = props.loadingFolders ?? loadingFoldersFromStore
  const loadingCategories = useSelector(
    state => state.files.loadingCategories || new Set()
  )
  const isLoadingAllFiles = useSelector(
    state => state.files.isLoadingAllFiles || false
  )
  const loadingProgressFromStore = useSelector(selectLoadingProgress)
  const loadingProgress = props.loadingProgress ?? loadingProgressFromStore
  const isLoadingBpFilesFromStore = useSelector(
    state => state.files.isLoadingBpFiles || false
  )
  const isLoadingBpFiles = props.isLoadingFiles ?? isLoadingBpFilesFromStore

  // Debug logging for filesCounts
  useEffect(() => {
    if (filesCounts) {
      console.log('📊 filesCounts loaded:', filesCounts)
    }
  }, [filesCounts])

  const [activeTab, setActiveTab] = useState(() => {
    console.log('UnifiedFiles: Initializing activeTab state')
    return 0
  })
  const [expandedFolders, setExpandedFolders] = useState(new Set())
  const [selectedFolder, setSelectedFolder] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedCustomBpId, setSelectedCustomBpId] = useState(() => props.customBuildings?.[0]?.id ?? null)
  const [selectedSourceBpId, setSelectedSourceBpId] = useState(null)
  const [customBpLoading, setCustomBpLoading] = useState(false)
  const [customBpError, setCustomBpError] = useState(null)

  useEffect(() => {
    props.onSelectFolder?.(selectedFolder?.id ?? null)
  }, [selectedFolder?.id, props.onSelectFolder])

  useEffect(() => {
    props.onSelectCategory?.(selectedCategory?.id ?? null)
  }, [selectedCategory?.id, props.onSelectCategory])

  const [hasAutoSelected, setHasAutoSelected] = useState(false)
  const [needsAttentionCount, setNeedsAttentionCount] = useState(0)
  const currentUser = useSelector(getCurrentUser)
  const canEdit = props.canEdit ?? canEditBp(currentUser, bp)

  useEffect(() => {
    if (!bp?.id) return
    let active = true
    getFileAnalysisSummary(bp.id)
      .then(res => {
        if (!active) return
        const c = res?.data?.data || {}
        setNeedsAttentionCount((c.wrongBuilding || 0) + (c.corrupted || 0))
      })
      .catch(() => {})
    return () => { active = false }
  }, [bp?.id])
  const isOwner =
    bp?.owners?.some(owner => owner.id === currentUser?.id) ||
    bp?.owner?.id === currentUser?.id
  const [folderUserCounts, setFolderUserCounts] = useState({})
  const [selectedFiles, setSelectedFiles] = useState([])
  const [infoPanelOpen, setInfoPanelOpen] = useState(false)
  const [checkFilesOpen, setCheckFilesOpen] = useState(false)
  const [unitFilingOpen, setUnitFilingOpen] = useState(false)
  const dispatch = useDispatch()

  useEffect(() => {
    if (customBuildings?.length && !customBuildings.some(b => b.id === selectedCustomBpId)) {
      setSelectedCustomBpId(customBuildings[0].id)
    }
  }, [customBuildings, selectedCustomBpId])

  useEffect(() => {
    if (!customBuildings?.length) return
    if (activeTab !== 1) return
    if (!selectedCustomBpId) return
    if (selectedBpFromStore?.id === selectedCustomBpId) {
      setCustomBpError(null)
      setCustomBpLoading(false)
      return
    }
    let cancelled = false
    setCustomBpError(null)
    setCustomBpLoading(true)
    api.get(`/building-passport/${selectedCustomBpId}`)
      .then(res => {
        if (cancelled) return
        dispatch(fetchBpRoutine.success(res.data.data))
      })
      .catch(err => {
        if (cancelled) return
        setCustomBpError(err?.response?.status === 403 ? 'forbidden' : 'error')
      })
      .finally(() => {
        if (!cancelled) setCustomBpLoading(false)
      })
    return () => { cancelled = true }
  }, [customBuildings, activeTab, selectedCustomBpId, selectedBpFromStore?.id, dispatch])

  const [searchCriteria, setSearchCriteria] = useState(null)
  const aggregateSearchTerm = useMemo(() => {
    if (!isAggregate) return null
    const q = (searchCriteria?.query || '').trim()
    return q.length >= 2 ? q : null
  }, [isAggregate, searchCriteria?.query])

  const aggregateActiveFilters = useMemo(() => {
    if (!isAggregate) return null
    const fileTypeIds = searchCriteria?.fileTypeIds || []
    const uploaderIds = searchCriteria?.uploaderIds || []
    const tagIds = searchCriteria?.tagIds || []
    const hasFilters = fileTypeIds.length > 0 || uploaderIds.length > 0 || tagIds.length > 0
    if (!aggregateSearchTerm && !hasFilters) return null
    return { search: aggregateSearchTerm, fileTypeIds, uploaderIds, tagIds }
  }, [isAggregate, aggregateSearchTerm, searchCriteria?.fileTypeIds, searchCriteria?.uploaderIds, searchCriteria?.tagIds])

  const aggregateCountsActive = !!aggregateActiveFilters

  const isIdentitySelected = selectedFolder?.id === IDENTITY_FOLDER_ID
  const isAllFilesSelected = selectedFolder?.id === 'all_files'
  const identityInAllFiles = isAllFilesSelected && filesCounts?.identityDocuments?.canView === true && Number(filesCounts?.identityDocuments?.count) > 0
  const identity = useIdentityDocumentsFolder({
    context,
    scopeId: isAggregate ? aggregateEntityId : bp?.id,
    searchCriteria: isAggregate || searchCriteria?.isActive ? searchCriteria : null,
    sourceBpId: isAggregate ? selectedSourceBpId : null,
    countsSummary: filesCounts?.identityDocuments,
    active: (isIdentitySelected || identityInAllFiles) && activeTab === 0
  })
  const identityLockedForUser = (identity.summary?.canView ?? identity.totalSummary?.canView) === false

  const identitySearchMatches = identity.isFiltered ? identity.summary?.count : null
  const identityHiddenBySearch = !!searchCriteria?.isActive && (identityLockedForUser || identitySearchMatches === 0)

  useEffect(() => {
    if (isIdentitySelected && identityHiddenBySearch) {
      handleFolderSelect({ id: 'all_files', name: 'All Files', isVirtual: true })
    }
  }, [isIdentitySelected, identityHiddenBySearch])

  const currentSelectionKey = (() => {
    if (selectedFolder?.id && selectedFolder.id !== 'all_files' && selectedFolder.id !== 'uncategorised') {
      return selectedFolder.id
    }
    if (selectedFolder?.id === 'uncategorised') return 'uncategorised'
    if (selectedCategory?.id) return selectedCategory.id
    return 'all_files'
  })()

  const portfolioCursorForKey = useSelector(selectPortfolioCursor(currentSelectionKey))
  const estateCursorForKey = useSelector(selectEstateCursor(currentSelectionKey))
  const aggregateCursor = isPortfolioAggregate ? portfolioCursorForKey : isEstateAggregate ? estateCursorForKey : null

  const searchTouchedKeysRef = useRef(new Set())

  const applyAggregateFilterExtras = (extra, key) => {
    if (aggregateSearchTerm) {
      extra.search = aggregateSearchTerm
      searchTouchedKeysRef.current.add(key)
    }
    if (aggregateActiveFilters) {
      if (aggregateActiveFilters.fileTypeIds.length) extra.fileTypeIds = aggregateActiveFilters.fileTypeIds
      if (aggregateActiveFilters.uploaderIds.length) extra.uploaderIds = aggregateActiveFilters.uploaderIds
      if (aggregateActiveFilters.tagIds.length) extra.tagIds = aggregateActiveFilters.tagIds
      searchTouchedKeysRef.current.add(key)
    }
  }

  const dispatchAggregateFolder = useCallback((folderId, cursor = null) => {
    if (!aggregateEntityId) return
    const extra = {}
    if (selectedSourceBpId) extra.buildingPassportId = selectedSourceBpId
    applyAggregateFilterExtras(extra, folderId)
    if (isPortfolioAggregate) {
      dispatch(loadPortfolioFilesByFolderRoutine({ portfolioId: aggregateEntityId, folderId, fileCategories, cursor, ...extra }))
    } else if (isEstateAggregate) {
      dispatch(loadEstateFilesByFolderRoutine({ estateId: aggregateEntityId, folderId, fileCategories, cursor, ...extra }))
    }
  }, [aggregateEntityId, isPortfolioAggregate, isEstateAggregate, dispatch, fileCategories, selectedSourceBpId, aggregateSearchTerm, aggregateActiveFilters])

  const dispatchAggregateCategory = useCallback((categoryId, cursor = null) => {
    if (!aggregateEntityId) return
    const extra = {}
    if (selectedSourceBpId) extra.buildingPassportId = selectedSourceBpId
    applyAggregateFilterExtras(extra, categoryId)
    if (isPortfolioAggregate) {
      dispatch(loadPortfolioFilesByCategoryRoutine({ portfolioId: aggregateEntityId, categoryId, cursor, ...extra }))
    } else if (isEstateAggregate) {
      dispatch(loadEstateFilesByCategoryRoutine({ estateId: aggregateEntityId, categoryId, cursor, ...extra }))
    }
  }, [aggregateEntityId, isPortfolioAggregate, isEstateAggregate, dispatch, selectedSourceBpId, aggregateSearchTerm, aggregateActiveFilters])

  const loadFolder = (isAggregate && aggregateEntityId)
    ? (folderId => dispatchAggregateFolder(folderId))
    : (props.onLoadFolder ?? (folderId => dispatch(loadFilesByFolderRoutine({ bpId: bp.id, folderId, fileCategories }))))
  const loadCategory = (isAggregate && aggregateEntityId)
    ? (categoryId => dispatchAggregateCategory(categoryId))
    : (props.onLoadCategory ?? (categoryId => dispatch(loadFilesByCategoryRoutine({ bpId: bp.id, categoryId }))))
  const loadAll = (isAggregate && aggregateEntityId)
    ? (() => dispatchAggregateCategory('all_files'))
    : (props.onLoadAll ?? (() => dispatch(loadAllFilesRoutine({ bpId: bp.id }))))

  const cursorFromStore = useSelector(state => state.files.cursorByKey?.[currentSelectionKey] ?? null)
  const hasMoreFromStore = useSelector(state => state.files.hasMoreByKey?.[currentSelectionKey] ?? false)
  const hasMore = props.hasMore ?? hasMoreFromStore
  const isCurrentSelectionLoading =
    currentSelectionKey === 'all_files'
      ? isLoadingAllFiles
      : currentSelectionKey === 'uncategorised'
        ? loadingCategories.has('uncategorised')
        : loadingFolders.has(currentSelectionKey) || loadingCategories.has(currentSelectionKey)
  const loadingMore = props.loadingMore ?? isCurrentSelectionLoading
  const onLoadMoreDefault = () => {
    if (!bp?.id || !hasMore || isCurrentSelectionLoading) return
    if (selectedFolder && selectedFolder.id !== 'all_files' && selectedFolder.id !== 'uncategorised') {
      dispatch(loadFilesByFolderRoutine({ bpId: bp.id, folderId: selectedFolder.id, fileCategories, cursor: cursorFromStore }))
    } else if (selectedFolder?.id === 'uncategorised') {
      dispatch(loadFilesByCategoryRoutine({ bpId: bp.id, categoryId: 'uncategorised', cursor: cursorFromStore }))
    } else if (selectedCategory?.id) {
      dispatch(loadFilesByCategoryRoutine({ bpId: bp.id, categoryId: selectedCategory.id, cursor: cursorFromStore }))
    } else {
      dispatch(loadAllFilesRoutine({ bpId: bp.id, cursor: cursorFromStore }))
    }
  }
  const onLoadMoreAggregate = () => {
    if (!aggregateEntityId || !hasMore || loadingMore) return
    if (selectedFolder && selectedFolder.id !== 'all_files' && selectedFolder.id !== 'uncategorised') {
      dispatchAggregateFolder(selectedFolder.id, aggregateCursor)
    } else if (selectedFolder?.id === 'uncategorised') {
      dispatchAggregateCategory('uncategorised', aggregateCursor)
    } else if (selectedCategory?.id) {
      dispatchAggregateCategory(selectedCategory.id, aggregateCursor)
    } else {
      dispatchAggregateCategory('all_files', aggregateCursor)
    }
  }
  const onLoadMore = (isAggregate && aggregateEntityId)
    ? onLoadMoreAggregate
    : (props.onLoadMore ?? onLoadMoreDefault)

  useFilesRefresh(isAggregate ? null : bp?.id, () => reloadCurrentSelection())

  const refreshAfterProcessing = () => {
    if (!bp?.id) return
    dispatch(getBpFilesCountsRoutine({ id: bp.id }))
    reloadCurrentSelection()
  }

  const reloadCurrentSelection = () => {
    if (!bp?.id) return
    if (isIdentitySelected) {
      identity.reload()
      return
    }
    if (selectedFolder && selectedFolder.id !== 'all_files' && selectedFolder.id !== 'uncategorised') {
      loadFolder(selectedFolder.id)
    } else if (selectedCategory?.id) {
      loadCategory(selectedCategory.id)
    } else {
      loadAll()
    }
  }
  const [bulkShareModalOpen, setBulkShareModalOpen] = useState(false)
  const [bulkUnsharedModalOpen, setBulkUnsharedModalOpen] = useState(false)
  const [unshareAllModalOpen, setUnshareAllModalOpen] = useState(false)
  const bulkShareStatus = useSelector(selectBulkShareStatus)
  const { sharedCount, warning } = bulkShareStatus
  const connectedUsers = useSelector(state => state.bpDetails.sharedInternals)
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false)
  const hasFetchedRef = useRef(false)
  const hasAutoClearedRef = useRef(false)

  const [alreadySharedModalOpen, setAlreadySharedModalOpen] = useState(false)
  const [alreadySharedFiles, setAlreadySharedFiles] = useState([])

  const folderBeforeSearchRef = useRef(null)
  useEffect(() => {
    if (searchCriteria?.isActive) {
      if (selectedFolder?.id === IDENTITY_FOLDER_ID) return
      dispatch(cancelAutoViewLoadingRoutine())
      if (selectedFolder?.id !== 'all_files') {
        folderBeforeSearchRef.current = { folder: selectedFolder, category: selectedCategory }
      }
      setSelectedFolder({ id: 'all_files', name: 'All Files', isVirtual: true })
      setSelectedCategory(null)
    } else if (folderBeforeSearchRef.current) {
      const prev = folderBeforeSearchRef.current
      folderBeforeSearchRef.current = null
      setSelectedFolder(prev.folder)
      setSelectedCategory(prev.category)
      if (isAggregate) {
        const prevId = prev.folder?.id
        if (prev.folder && prevId !== 'all_files' && prevId !== 'uncategorised') {
          searchTouchedKeysRef.current.delete(prevId)
          loadFolder(prevId)
        } else if (prevId === 'uncategorised') {
          searchTouchedKeysRef.current.delete('uncategorised')
          loadCategory('uncategorised')
        } else if (prev.category?.id) {
          searchTouchedKeysRef.current.delete(prev.category.id)
          loadCategory(prev.category.id)
        } else {
          loadAll()
        }
      } else if (prev.folder && prev.folder.id !== 'all_files' && prev.folder.id !== 'uncategorised' &&
          !allFilesLoaded && !loadedFolders.has(prev.folder.id)) {
        loadFolder(prev.folder.id)
      }
    }
  }, [searchCriteria?.isActive, dispatch])

  const isFirstAggregateFilterRun = useRef(true)
  useEffect(() => {
    if (!isAggregate || !aggregateEntityId) return
    if (isFirstAggregateFilterRun.current) {
      isFirstAggregateFilterRun.current = false
      return
    }
    if (selectedFolder?.id === IDENTITY_FOLDER_ID) return
    if (aggregateSearchTerm) {
      setSelectedFolder({ id: 'all_files', name: 'All Files', isVirtual: true })
      setSelectedCategory(null)
      dispatchAggregateCategory('all_files')
      return
    }
    if (selectedFolder && selectedFolder.id !== 'all_files' && selectedFolder.id !== 'uncategorised') {
      dispatchAggregateFolder(selectedFolder.id)
    } else if (selectedFolder?.id === 'uncategorised') {
      dispatchAggregateCategory('uncategorised')
    } else if (selectedCategory?.id) {
      dispatchAggregateCategory(selectedCategory.id)
    } else {
      dispatchAggregateCategory('all_files')
    }
  }, [selectedSourceBpId, aggregateSearchTerm, aggregateActiveFilters])

  const files = useMemo(() => {
    if (isAggregate) {
      return applyAggregateClientFilters(allFiles, searchCriteria)
    }
    if (searchCriteria?.isActive) return searchResults
    return allFiles
  }, [allFiles, searchResults, searchCriteria, isAggregate])

  const [aggregateSearchCounts, setAggregateSearchCounts] = useState(null)
  useEffect(() => {
    if (!isAggregate || !aggregateEntityId || !aggregateActiveFilters) {
      setAggregateSearchCounts(null)
      return
    }
    let active = true
    const query = {}
    if (aggregateActiveFilters.search) query['filter[search]'] = aggregateActiveFilters.search
    if (aggregateActiveFilters.fileTypeIds.length) query['filter[file_type_ids]'] = aggregateActiveFilters.fileTypeIds.join(',')
    if (aggregateActiveFilters.uploaderIds.length) query['filter[uploader_ids]'] = aggregateActiveFilters.uploaderIds.join(',')
    if (aggregateActiveFilters.tagIds.length) query['filter[tag_ids]'] = aggregateActiveFilters.tagIds.join(',')
    if (currentUser?.hideDuplicateFiles ?? true) query['filter[hide_duplicates]'] = 1
    const request = isPortfolioAggregate
      ? getPortfolioFileCounts({ portfolioId: aggregateEntityId, query, buildingPassportId: selectedSourceBpId || undefined })
      : getEstateFileCounts({ estateId: aggregateEntityId, query, buildingPassportId: selectedSourceBpId || undefined })
    request
      .then(res => { if (active) setAggregateSearchCounts(res?.data?.data || null) })
      .catch(() => { if (active) setAggregateSearchCounts(null) })
    return () => { active = false }
  }, [isAggregate, aggregateEntityId, isPortfolioAggregate, aggregateActiveFilters, selectedSourceBpId, currentUser])

  const totalBpResults = useMemo(() => {
    if (isAggregate) {
      if (!aggregateCountsActive) return 0
      return aggregateSearchCounts?.total ?? files?.length ?? 0
    }
    if (!files || !searchCriteria?.query || searchCriteria.query.trim().length < 2) return 0
    return applyClientSearch(files, searchCriteria).length
  }, [isAggregate, aggregateCountsActive, aggregateSearchCounts, files, searchCriteria])

  // Individual filter states for better control
  const [searchQuery, setSearchQuery] = useState('')
  const [searchMode, setSearchMode] = useState(() => {
    const saved = localStorage.getItem('fileSearchMode')
    return saved || 'name' // Default to 'name' mode
  })
  const [activeCustomViewId, setActiveCustomViewId] = useState(null) // SharePoint view ID for search filtering
  const [hideDuplicates, setHideDuplicatesShared] = useHideDuplicates(stateKeyId)
  const toggleHideDuplicates = () => {
    setHideDuplicatesShared(!hideDuplicates)
    if (!isAggregate && bp?.id) {
      dispatch(clearBpFilesRoutine())
      loadAll()
      dispatch(getBpFilesCountsRoutine({ id: bp.id }))
    }
  }

  useEffect(() => {
    if (bp?.id) dispatch(getBpFilesCountsRoutine({ id: bp.id }))
  }, [bp?.id, dispatch])
  const [selectedUploaders, setSelectedUploaders] = useState([])
  const [selectedFileTypes, setSelectedFileTypes] = useState([])
  const [selectedFileTags, setSelectedFileTags] = useState([])
  const [selectedViewableBy, setSelectedViewableBy] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editingFile, setEditingFile] = useState(null)
  const [reclassifyModalOpen, setReclassifyModalOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)

  // Color palette - must match FileCategories.js and CustomViewFolders.js
  const colorPalette = [
    '#0c132b',
    '#bbeaf0',
    '#36becd',
    '#ffc111',
    '#cd2000',
    '#ffaf7f'
  ]

  const [showOnlyMyFiles, setShowOnlyMyFiles] = useState(false)
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
  const menuSlot = useFloatingSlot('files-menu', FLOATING_PRIORITY.menu, activeTab === 0)

  useEffect(() => {
    if (!hasFetchedRef.current && bp?.id && files?.length > 0) {
      const fileIds = files.map(f => f.id)
      dispatch(fetchSharedUsersForFilesRoutine.trigger(fileIds))
      hasFetchedRef.current = true
    }
  }, [bp?.id, files, dispatch])

  useEffect(() => {
    if (bp?.id && bulkShareModalOpen) {
      dispatch(getBpConnectedUsersRoutine(bp.id))
    }
  }, [bulkShareModalOpen, bp?.id, dispatch])

  useEffect(() => {
    if (
      !bulkShareModalOpen &&
      (sharedCount > 0 || (warning && warning.length > 0))
    ) {
      if (sharedCount > 0) {
        toast.success(
          `${sharedCount} file${sharedCount !== 1 ? 's' : ''} shared successfully!`
        )
      }

      if (warning?.length) {
        setAlreadySharedFiles(warning)

        toast.error(
          <ShortAlreadySharedToast
            count={warning.length}
            onViewDetails={() => setAlreadySharedModalOpen(true)}
          />,
          {
            autoClose: 8000,
            style: { maxWidth: 420 }
          }
        )
      }

      dispatch(bulkShareFilesRoutine.fulfill())
    }
  }, [bulkShareModalOpen, sharedCount, warning, dispatch])

  // Fetch folder user counts for permissions indicator
  const fetchFolderUserCounts = useCallback(() => {
    if (bp?.id && isOwner) {
      api.get(`/building-passport/${bp.id}/folders/user-counts`)
        .then(resp => {
          const data = resp?.data?.data || {}

          // Convert arrays to objects for easy lookup
          const folderCounts = (data.folders || []).reduce((acc, item) => {
            acc[item.id] = item.count
            return acc
          }, {})

          const categoryCounts = (data.categories || []).reduce((acc, item) => {
            acc[item.id] = item.count
            return acc
          }, {})

          setFolderUserCounts({
            folders: folderCounts,
            categories: categoryCounts
          })
        })
        .catch(err => {
          console.error('Failed to load folder user counts:', err)
        })
    }
  }, [bp?.id, isOwner])

  useEffect(() => {
    fetchFolderUserCounts()
  }, [fetchFolderUserCounts])

  // Restore view mode from cookies when BP is available or changes
  useEffect(() => {
    if (stateKeyId) {
      const savedViewMode = CookieService.getViewMode(stateKeyId)
      const targetTab = savedViewMode === 'custom' ? 1 : 0
      setActiveTab(targetTab)
    }
  }, [stateKeyId])

  const handleTabChange = (event, newValue) => {
    console.log('UnifiedFiles: Tab changing from', activeTab, 'to', newValue)
    setActiveTab(newValue)

    // Reset selection when switching to automatic view
    if (newValue === 0) {
      setHasAutoSelected(false) // Allow auto-selection to run again
      setSelectedFolder(null)
      setSelectedCategory(null)
      // Cancel any in-flight custom folder loads
      dispatch(invalidateCustomFolderCacheRoutine({ invalidateAll: true }))
    }

    // Save view mode to cookies
    const viewMode = newValue === 1 ? 'custom' : 'automatic'
    CookieService.setViewMode(stateKeyId, viewMode)
  }

  const toggleFolderExpanded = folderId => {
    const newExpanded = new Set(expandedFolders)
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId)
    } else {
      newExpanded.add(folderId)
    }
    setExpandedFolders(newExpanded)

    // Save expanded folders to localStorage instead of cookies (to avoid 431 errors)
    if (stateKeyId) {
      try {
        localStorage.setItem(
          `expandedFolders_${stateKeyId}`,
          JSON.stringify([...newExpanded])
        )
      } catch (e) {
        console.warn('Failed to save expanded folders to localStorage:', e)
      }
    }
  }

  const [justClickedAllFiles, setJustClickedAllFiles] = useState(false)

  const handleFolderSelect = (folder, fromRightPanel = false) => {
    // Cancel any in-flight automatic view file loads
    dispatch(cancelAutoViewLoadingRoutine())

    if (!folder) {
      if (stateKeyId) {
        CookieService.remove(`selectedFolder_${stateKeyId}`)
        CookieService.remove(`selectedCategory_${stateKeyId}`)
      }
      setSelectedFolder(null)
      setSelectedCategory(null)
      return
    }

    if (folder.id === IDENTITY_FOLDER_ID) {
      setSelectedFolder(IDENTITY_FOLDER)
      setSelectedCategory(null)
      if (stateKeyId) {
        CookieService.remove(`selectedFolder_${stateKeyId}`)
        CookieService.remove(`selectedCategory_${stateKeyId}`)
      }
      return
    }

    if (folder.id === 'all_files') {
      console.log('🟢 Switching to virtual folder: All Files')
      setSelectedFolder({
        id: 'all_files',
        name: 'All Files',
        isVirtual: true
      })
      setSelectedCategory(null)
      setJustClickedAllFiles(true)

      if (stateKeyId) {
        CookieService.set(`selectedFolder_${stateKeyId}`, 'all_files')
        CookieService.remove(`selectedCategory_${stateKeyId}`)
      }

      setExpandedFolders(prev => new Set(prev).add('all_files'))

      if (isAggregate) {
        if (aggregateCountsActive || !allFilesLoaded) {
          loadAll()
        }
      } else if (!allFilesLoaded && !searchCriteria?.isActive) {
        loadAll()
      }
      return
    }

    // Check file count - don't auto-select folders with > 500 files unless explicitly clicked from tree
    const fileCount = folder?.fileCount || folder?.file_count || 0
    if (fileCount > 500 && !fromRightPanel) {
      console.log(`⚠️ Folder has ${fileCount} files (> 500), skipping auto-selection. User must manually click.`)
      return
    }

    console.log(`📁 Selecting folder: ${folder.name}`)

    setSelectedFolder(folder)
    setSelectedCategory(null)

    if (stateKeyId) {
      CookieService.set(`selectedFolder_${stateKeyId}`, folder.id)
      CookieService.remove(`selectedCategory_${stateKeyId}`)
      // Save to localStorage instead of cookies
      try {
        localStorage.setItem(
          `expandedFolders_${stateKeyId}`,
          JSON.stringify([...expandedFolders, folder.id])
        )
      } catch (e) {
        console.warn('Failed to save expanded folders:', e)
      }
    }

    if (isAggregate) {
      if (aggregateCountsActive) {
        loadFolder(folder.id)
      } else if ((!allFilesLoaded && !loadedFolders.has(folder.id)) || searchTouchedKeysRef.current.has(folder.id)) {
        searchTouchedKeysRef.current.delete(folder.id)
        loadFolder(folder.id)
      }
    } else if (!searchCriteria?.isActive && !allFilesLoaded && !loadedFolders.has(folder.id)) {
      loadFolder(folder.id)
    }

    if (fromRightPanel) {
      setExpandedFolders(prev => new Set(prev).add(folder.id))
    } else {
      // Toggle when clicked from left tree
      toggleFolderExpanded(folder.id)
    }
  }

  const handleCategorySelect = category => {
    // Cancel any in-flight automatic view file loads
    dispatch(cancelAutoViewLoadingRoutine())

    // Handle null category (clearing selection)
    if (!category) {
      setSelectedCategory(null)
      setSelectedFolder(null)
      if (stateKeyId) {
        CookieService.remove(`selectedFolder_${stateKeyId}`)
        CookieService.remove(`selectedCategory_${stateKeyId}`)
      }
      return
    }

    // Handle "uncategorised" — load only uncategorised files via filter[uncategorised]=true
    if (category.id === 'uncategorised') {
      console.log('Switching to uncategorised files')
      setSelectedFolder({
        id: 'uncategorised',
        name: 'Uncategorised Files',
        isVirtual: true
      })
      setSelectedCategory(null)

      if (stateKeyId) {
        CookieService.set(`selectedFolder_${stateKeyId}`, 'uncategorised')
        CookieService.remove(`selectedCategory_${stateKeyId}`)
      }

      // Load only uncategorised files (not all files)
      if (!allFilesLoaded && !loadedCategories.has('uncategorised') && !loadedFolders.has('uncategorised')) {
        console.log('📥 Loading uncategorised files...')
        loadCategory('uncategorised')
      }
      return
    }

    // Set state for regular categories
    setSelectedCategory(category)
    setSelectedFolder(null)

    if (isAggregate) {
      if (aggregateCountsActive) {
        loadCategory(category.id)
      } else if ((!allFilesLoaded && !loadedCategories.has(category.id) && !loadedFolders.has(category.id)) || searchTouchedKeysRef.current.has(category.id)) {
        searchTouchedKeysRef.current.delete(category.id)
        loadCategory(category.id)
      }
    } else if (!searchCriteria?.isActive && !allFilesLoaded && !loadedCategories.has(category.id) && !loadedFolders.has(category.id)) {
      loadCategory(category.id)
    }

    // Save selected category to cookies for automatic view
    if (stateKeyId) {
      CookieService.set(`selectedCategory_${stateKeyId}`, category.id)
      CookieService.remove(`selectedFolder_${stateKeyId}`) // Clear folder when category is selected

      // Also ensure the containing folder is expanded and save that state
      const containingFolder = folders.find(
        folder => folder.id === category.folderId
      )
      if (containingFolder) {
        setExpandedFolders(prev => {
          const newExpanded = new Set(prev)
          newExpanded.add(containingFolder.id)
          // Save to localStorage instead of cookies
          try {
            localStorage.setItem(
              `expandedFolders_${stateKeyId}`,
              JSON.stringify([...newExpanded])
            )
          } catch (e) {
            console.warn('Failed to save expanded folders:', e)
          }
          return newExpanded
        })
      }
    }
  }

  const handleEditModalOpen = fileToEdit => {
    setEditingFile(fileToEdit)
    setEditModalOpen(true)
  }

  // Count uncategorised files
  const uncategorisedCount = useMemo(() => {
    // Use API counts if files not loaded
    if (!files || files.length === 0) {
      return filesCounts?.byCategory?.['uncategorised'] || 0
    }
    return files.filter(file => !file.fileType?.id).length
  }, [files, filesCounts])

  // Auto-select first folder on initial load (only if no saved state)
  // Only auto-select if:
  // - We haven't already auto-selected
  // - Folders are loaded (don't wait for files!)
  // - No folder/category is currently selected
  // - We're in automatic view (tab 0)
  useEffect(() => {
    if (
      !automaticTabContent &&
      !hasAutoSelected &&
      fileCategories?.length > 0 &&
      folders?.length > 0 &&
      !selectedFolder &&
      !selectedCategory &&
      activeTab === 0 &&
      !searchCriteria?.isActive &&
      stateKeyId
    ) {
      console.log('🔍 Auto-selecting folder and loading files...')

      // Check if we should select "All Files" (from Documents folder click)
      const shouldSelectAllFiles = CookieService.get(`selectAllFiles_${stateKeyId}`)
      if (shouldSelectAllFiles === 'true') {
        console.log('🟢 Auto-selecting All Files from Documents folder click')
        CookieService.remove(`selectAllFiles_${stateKeyId}`) // Clear the flag
        handleFolderSelect({
          id: 'all_files',
          name: 'All Files',
          isVirtual: true
        })
        setHasAutoSelected(true)
        return
      }

      // Try to restore saved folder from cookies
      let folderToSelect = null
      const savedFolderId = CookieService.get(`selectedFolder_${stateKeyId}`)

      if (savedFolderId) {
        // Try to find the saved folder
        const savedFolder = folders.find(f => f.id === savedFolderId)
        if (savedFolder) {
          // Check file count - only restore if <= 500 files
          const fileCount = savedFolder?.filesCount || savedFolder?.fileCount || savedFolder?.file_count || 0
          console.log('🔍 Saved folder file count check:', {
            folderName: savedFolder.name,
            fileCount,
            filesCount: savedFolder?.filesCount,
            savedFolder
          })
          if (fileCount <= 500) {
            folderToSelect = savedFolder
            console.log(
              'Restored saved folder from cache:',
              savedFolder.name,
              `(${fileCount} files)`
            )
          } else {
            console.log(
              `Saved folder has ${fileCount} files (> 500), skipping auto-load. User must manually select.`
            )
            setHasAutoSelected(true) // Prevent infinite loop
            return
          }
        } else {
          console.log('⚠️ Saved folder not found, checking first folder')
        }
      }

      // If no saved folder or saved folder not found, select the first one (with file count check)
      if (!folderToSelect && folders.length > 0) {
        const firstFolder = folders[0]
        const fileCount = firstFolder?.filesCount || firstFolder?.fileCount || firstFolder?.file_count || 0

        if (fileCount <= 500) {
          folderToSelect = firstFolder
          console.log('✅ Auto-selected first folder:', firstFolder.name, `(${fileCount} files)`)
        } else {
          console.log(
            `⚠️ First folder has ${fileCount} files (> 500), skipping auto-load. User must manually select.`
          )
          setHasAutoSelected(true) // Prevent infinite loop
          return
        }
      }

      // If we found a folder, select it AND load files
      if (folderToSelect) {
        setSelectedFolder(folderToSelect)
        setHasAutoSelected(true)

        // Expand it in the tree
        setExpandedFolders(prev => new Set([...prev, folderToSelect.id]))

        // Save to localStorage for persistence
        CookieService.set(`selectedFolder_${stateKeyId}`, folderToSelect.id)

        // Load files for this folder
        if (!allFilesLoaded && !loadedFolders.has(folderToSelect.id)) {
          console.log(
            '📥 Loading files for auto-selected folder:',
            folderToSelect.name
          )
          loadFolder(folderToSelect.id)
        } else {
          console.log(
            'Files already loaded for folder:',
            folderToSelect.name
          )
        }
      } else {
        // No folders available - mark as auto-selected anyway to prevent loop
        console.log('No folders found')
        setHasAutoSelected(true)
      }
    }
  }, [
    folders,
    fileCategories,
    hasAutoSelected,
    selectedFolder,
    selectedCategory,
    activeTab,
    searchCriteria?.isActive,
    stateKeyId,
    dispatch,
    allFilesLoaded,
    loadedFolders
  ])

  const getFolderFileCount = useCallback(
    folder => {
      if (!fileCategories) return 0

      // Aggregate search/filter: matches come from the server counts, not the loaded page.
      // Until those counts arrive, fall through to the loaded-files estimate below.
      if (aggregateCountsActive && aggregateSearchCounts?.byCategory) {
        const byCat = aggregateSearchCounts.byCategory
        if (folder.id === 'uncategorised') return byCat['uncategorised'] || 0
        return fileCategories
          .filter(cat => cat.folderId === folder.id)
          .reduce((sum, cat) => sum + (byCat[cat.id?.toLowerCase()] || 0), 0)
      }

      // WHEN SEARCHING: Always count from search results (files array)
      if (searchCriteria?.isActive && files) {
        if (folder.id === 'uncategorised') {
          return files.filter(f => !f.fileType?.id).length
        }

        const folderCategoryIds = fileCategories
          .filter(cat => cat.folderId === folder.id)
          .map(cat => cat.id)

        return files.filter(f =>
          folderCategoryIds.includes(
            pathOr('', ['fileType', 'fileCategoryId'], f)
          )
        ).length
      }

      // WHEN NOT SEARCHING: Only count from loaded files if ALL files loaded OR this specific folder is loaded
      const canCountFromFiles =
        (allFilesLoaded || loadedFolders.has(folder.id)) &&
        files &&
        files.length > 0

      if (canCountFromFiles) {
        if (folder.id === 'uncategorised') {
          const uncategorised = files.filter(f => !f.fileType?.id)
          return uncategorised.length
        }

        const folderCategoryIds = fileCategories
          .filter(cat => cat.folderId === folder.id)
          .map(cat => cat.id)

        const folderFiles = files.filter(f =>
          folderCategoryIds.includes(
            pathOr('', ['fileType', 'fileCategoryId'], f)
          )
        )

        return folderFiles.length
      }

      // If files NOT loaded for this folder, use counts from API (metadata only)
      if (filesCounts?.byCategory) {
        if (folder.id === 'uncategorised') {
          return filesCounts.byCategory['uncategorised'] || 0
        }

        // Sum up all category counts for this folder
        const folderCategoryIds = fileCategories
          .filter(cat => cat.folderId === folder.id)
          .map(cat => cat.id)

        return folderCategoryIds.reduce((sum, catId) => {
          // Normalize UUID to lowercase for lookup
          const normalizedId = catId?.toLowerCase()
          return sum + (filesCounts.byCategory[normalizedId] || 0)
        }, 0)
      }

      return 0
    },
    [
      files,
      fileCategories,
      searchCriteria?.isActive,
      filesCounts,
      allFilesLoaded,
      loadedFolders,
      isAggregate,
      aggregateCountsActive,
      aggregateSearchCounts
    ]
  )

  // Auto-navigation on search disabled — user's selected folder/category stays
  // sticky during search. The file grid filters search results client-side.
  useEffect(() => {
    if (!searchCriteria?.isActive) {
      hasAutoClearedRef.current = false
    }
  }, [searchCriteria?.isActive])

  const getFolderTotalFileCount = useCallback(
    folder => {
      if (!fileCategories) {
        return 0
      }

      // Aggregate: total-in-folder always comes from the full server counts,
      // regardless of search or how many files are loaded
      if (isAggregate) {
        if (folder.id === 'all' || folder.id === 'all_files') {
          return filesCounts?.total || 0
        }
        if (filesCounts?.byCategory) {
          if (folder.id === 'uncategorised') {
            return filesCounts.byCategory['uncategorised'] || 0
          }
          return fileCategories
            .filter(cat => cat.folderId === folder.id)
            .reduce((sum, cat) => sum + (filesCounts.byCategory[cat.id?.toLowerCase()] || 0), 0)
        }
        return 0
      }

      // WHEN SEARCHING: Always count from search results (files array)
      if (searchCriteria?.isActive && files) {
        if (folder.id === 'all' || folder.id === 'all_files') {
          return files.length
        }

        if (folder.id === 'uncategorised') {
          return files.filter(file => !file.fileType?.id).length
        }

        const folderCategoryIds = fileCategories
          .filter(cat => cat.folderId === folder.id)
          .map(cat => cat.id)

        return files.filter(file =>
          folderCategoryIds.includes(
            pathOr('', ['fileType', 'fileCategoryId'], file)
          )
        ).length
      }

      // WHEN NOT SEARCHING: Use normal logic below

      // For "All Files" - use API counts
      if (folder.id === 'all' || folder.id === 'all_files') {
        return filesCounts?.total || 0
      }

      // If ALL files are loaded, count from loaded files array
      if (!filesCounts?.byCategory && allFilesLoaded && files && files.length > 0) {
        if (folder.id === 'uncategorised') {
          return files.filter(file => !file.fileType?.id).length
        }

        const folderCategoryIds = fileCategories
          .filter(cat => cat.folderId === folder.id)
          .map(cat => cat.id)

        return files.filter(file =>
          folderCategoryIds.includes(
            pathOr('', ['fileType', 'fileCategoryId'], file)
          )
        ).length
      }

      // If this specific folder is loaded, count from loaded files
      if (!filesCounts?.byCategory && loadedFolders.has(folder.id) && files && files.length > 0) {
        if (folder.id === 'uncategorised') {
          return files.filter(file => !file.fileType?.id).length
        }

        const folderCategoryIds = fileCategories
          .filter(cat => cat.folderId === folder.id)
          .map(cat => cat.id)

        return files.filter(file =>
          folderCategoryIds.includes(
            pathOr('', ['fileType', 'fileCategoryId'], file)
          )
        ).length
      }

      // Otherwise, use counts from API (metadata only)
      if (filesCounts?.byCategory) {
        if (folder.id === 'uncategorised') {
          return filesCounts.byCategory['uncategorised'] || 0
        }

        // Sum up all category counts for this folder
        const folderCategoryIds = fileCategories
          .filter(cat => cat.folderId === folder.id)
          .map(cat => cat.id)

        const count = folderCategoryIds.reduce((sum, catId) => {
          // Normalize UUID to lowercase for lookup
          const normalizedId = catId?.toLowerCase()
          const catCount = filesCounts.byCategory[normalizedId] || 0
          return sum + catCount
        }, 0)

        return count
      }

      return 0
    },
    [files, fileCategories, filesCounts, allFilesLoaded, loadedFolders, searchCriteria?.isActive, isAggregate]
  )

  const selectedCategoryTotal = useMemo(() => {
    if (!selectedCategory?.id) return 0
    if (searchCriteria?.isActive && files) {
      return files.filter(file => pathOr('', ['fileType', 'fileCategoryId'], file) === selectedCategory.id).length
    }
    if (filesCounts?.byCategory) {
      const normalizedId = selectedCategory.id?.toLowerCase()
      return filesCounts.byCategory[normalizedId] || 0
    }
    if (allFilesLoaded && files && files.length > 0) {
      return files.filter(file => pathOr('', ['fileType', 'fileCategoryId'], file) === selectedCategory.id).length
    }
    return 0
  }, [selectedCategory, searchCriteria?.isActive, files, filesCounts, allFilesLoaded])

  const aggregateSearchSelectionTotal = useMemo(() => {
    const globalTotal = aggregateSearchCounts?.total ?? (files?.length || 0)
    const byCat = aggregateSearchCounts?.byCategory
    if (selectedFolder && selectedFolder.id !== 'all_files' && selectedFolder.id !== 'uncategorised') {
      if (!byCat) return files?.length || 0
      const folderCategoryIds = fileCategories
        .filter(cat => cat.folderId === selectedFolder.id)
        .map(cat => cat.id)
      return folderCategoryIds.reduce((sum, catId) => sum + (byCat[catId?.toLowerCase()] || 0), 0)
    }
    if (selectedFolder?.id === 'uncategorised') {
      return byCat?.['uncategorised'] ?? (files?.length || 0)
    }
    if (selectedCategory?.id) {
      return byCat?.[selectedCategory.id?.toLowerCase()] ?? (files?.length || 0)
    }
    return globalTotal
  }, [aggregateSearchCounts, selectedFolder, selectedCategory, fileCategories, files])

  const folderHasSearchMatches = useCallback((folderId) => {
    const byCat = aggregateSearchCounts?.byCategory
    if (!aggregateCountsActive || !byCat) return false
    if (folderId === 'uncategorised') return (byCat['uncategorised'] || 0) > 0
    return fileCategories
      .filter(cat => cat.folderId === folderId)
      .some(cat => (byCat[cat.id?.toLowerCase()] || 0) > 0)
  }, [aggregateCountsActive, aggregateSearchCounts, fileCategories])

  const categoryHasSearchMatches = useCallback((categoryId) => {
    const byCat = aggregateSearchCounts?.byCategory
    if (!aggregateCountsActive || !byCat) return false
    return (byCat[categoryId?.toLowerCase()] || 0) > 0
  }, [aggregateCountsActive, aggregateSearchCounts])

  const searchMatchCountForCategory = useCallback((categoryId, loadedFallback) => {
    if (aggregateCountsActive) {
      const byCat = aggregateSearchCounts?.byCategory
      if (!byCat) return loadedFallback
      return byCat[categoryId?.toLowerCase()] || 0
    }
    return loadedFallback
  }, [aggregateCountsActive, aggregateSearchCounts])

  const searchMatchCountAllFiles = useCallback((loadedFallback) => {
    if (aggregateCountsActive) return aggregateSearchCounts?.total ?? loadedFallback
    return loadedFallback
  }, [aggregateCountsActive, aggregateSearchCounts])

  const searchMatchCountUncategorised = useCallback((loadedFallback) => {
    if (aggregateCountsActive) {
      const byCat = aggregateSearchCounts?.byCategory
      if (!byCat) return loadedFallback
      return byCat['uncategorised'] || 0
    }
    return loadedFallback
  }, [aggregateCountsActive, aggregateSearchCounts])

  const tickMeansMatches = aggregateCountsActive || (!isAggregate && !!searchCriteria?.isActive)

  const folderShowsTick = useCallback((folder) => {
    if (aggregateCountsActive) return folderHasSearchMatches(folder.id)
    if (!isAggregate && searchCriteria?.isActive) return getFolderFileCount(folder) > 0
    return allFilesLoaded || loadedFolders.has(folder.id)
  }, [aggregateCountsActive, isAggregate, folderHasSearchMatches, searchCriteria?.isActive, getFolderFileCount, allFilesLoaded, loadedFolders])

  const categoryShowsTick = useCallback((category) => {
    if (aggregateCountsActive) return categoryHasSearchMatches(category.id)
    if (!isAggregate && searchCriteria?.isActive) {
      return !!files && files.some(f => pathOr('', ['fileType', 'fileCategoryId'], f) === category.id)
    }
    return allFilesLoaded || loadedCategories.has(category.id) || loadedFolders.has(category.id)
  }, [aggregateCountsActive, isAggregate, categoryHasSearchMatches, searchCriteria?.isActive, files, allFilesLoaded, loadedCategories, loadedFolders])

  const computedSelectionTotal = useMemo(() => {
    if (aggregateCountsActive) return aggregateSearchSelectionTotal
    if (selectedFolder) return getFolderTotalFileCount(selectedFolder)
    if (selectedCategory?.id) return selectedCategoryTotal
    return filesCounts?.total || 0
  }, [aggregateCountsActive, aggregateSearchSelectionTotal, selectedFolder, selectedCategory, selectedCategoryTotal, getFolderTotalFileCount, filesCounts])

  const total = props.total ?? computedSelectionTotal

  useEffect(() => {
    if (!searchCriteria?.isActive || !searchCriteria?.query) {
      if (!selectedFolder && !selectedCategory) {
        setHasAutoSelected(false)
      }
    }
  }, [searchCriteria, selectedFolder, selectedCategory])

  const visibleFiles = useMemo(() => {
    if (!files) return []

    let filtered = files

    // Apply "Viewable By" filter (local-only filter, not handled by Bedrock KB)
    if (selectedViewableBy && selectedViewableBy.length > 0) {
      const userId = selectedViewableBy[0]
      // Find the user in sharedInternals to get their fileIds
      const user = connectedUsers?.find(u => u.id === userId)
      if (user && user.fileIds) {
        // Filter files to only show those that this user can view
        filtered = filtered.filter(f => user.fileIds.includes(f.id))
      } else {
        // If user not found or has no fileIds, show no files
        filtered = []
      }
    }

    // When search is active, files are already filtered by Bedrock KB
    // No additional filtering needed!

    return filtered
  }, [files, selectedViewableBy, connectedUsers, searchCriteria?.isActive])

  const sourceBpFilterOptions = useMemo(() => {
    if (!isAggregate) return []
    if (customBuildings?.length) {
      return customBuildings.map(b => ({
        value: b.id,
        label: b.name || 'Unnamed building',
        sublabel: b.address || undefined,
        group: b.estateName || undefined,
      }))
    }
    const seen = new Map()
    ;(allFiles || []).forEach(f => {
      const b = f.buildingPassport
      if (b?.id && !seen.has(b.id)) seen.set(b.id, { value: b.id, label: b.name || 'Unnamed building' })
    })
    return [...seen.values()].sort((a, b) => String(a.label).localeCompare(String(b.label)))
  }, [isAggregate, customBuildings, allFiles])

  const identityHostFolder = (folders || []).find(folder => /^occupation/i.test(folder?.name || ''))
  const identityHasDocuments = (identity.totalSummary?.count ?? 0) > 0
  const isIdentityHost = folder => !!identityHostFolder && folder?.id === identityHostFolder.id && identityHasDocuments && !identityHiddenBySearch
  const identitySearching = !!(identity.isFiltered || searchCriteria?.isActive)
  const identityCountable = identityHasDocuments && (identity.summary?.canView ?? identity.totalSummary?.canView) === true
  const identityTotalExtra = identityCountable ? identity.totalSummary.count : 0
  const identityMatchExtra = !identityCountable ? 0 : identitySearching ? (identitySearchMatches ?? 0) : identityTotalExtra
  const isIdentityParent = folder => !!identityHostFolder && folder?.id === identityHostFolder.id

  const renderIdentityTreeFolder = ({ nested = false, folderColor } = {}) => {
    const totalCount = identity.totalSummary?.count ?? 0
    if (!identity.totalSummary || totalCount === 0 || identityHiddenBySearch) return null
    if (!nested && identityHostFolder) return null
    const identityColor = '#33becc'
    const locked = identity.totalSummary.canView === false
    const showMatches = identitySearching && !locked && identitySearchMatches != null
    const displayCount = !identitySearching
      ? `(${totalCount})`
      : showMatches
        ? `(${identitySearchMatches}/${totalCount})`
        : null
    const tooltip = locked
      ? 'Personal ID documents. You need identity document access to open them.'
      : showMatches
        ? `${identitySearchMatches} matching / ${totalCount} total. ${IDENTITY_SEARCH_HINT}`
        : identitySearching
          ? IDENTITY_SEARCH_HINT
          : `${totalCount} total`
    const countLabel = !identitySearching
      ? `, ${totalCount} ${totalCount === 1 ? 'document' : 'documents'}`
      : showMatches
        ? `, ${identitySearchMatches} matching of ${totalCount}`
        : ''
    const ariaLabel = `Identity documents${countLabel}${locked ? ', locked' : ''}`
    const onKeyDown = e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        handleFolderSelect(IDENTITY_FOLDER)
      }
    }
    const lockIcon = locked && (
      <Tooltip title='Locked. Ask your administrator for identity document access.' placement='top'>
        <LockOutlinedIcon style={{ fontSize: 13, color: '#475569', marginLeft: 4, flexShrink: 0 }} aria-label='Locked' />
      </Tooltip>
    )
    if (nested) {
      return (
        <TreeSubFolder
          key={IDENTITY_FOLDER_ID}
          isSelected={isIdentitySelected}
          folderColor={folderColor}
          onClick={e => {
            e.stopPropagation()
            handleFolderSelect(IDENTITY_FOLDER)
          }}
          role='button'
          tabIndex={0}
          aria-label={ariaLabel}
          onKeyDown={onKeyDown}
        >
          <CategoryIcon folderColor={folderColor}>
            <BadgeIcon />
          </CategoryIcon>
          <CategoryContent>
            <CategoryName>Identity documents</CategoryName>
            {displayCount && (
              <Tooltip title={tooltip}>
                <CategoryFileCount folderColor={folderColor} data-testid='identity-folder-count'>
                  {displayCount}
                </CategoryFileCount>
              </Tooltip>
            )}
            {lockIcon}
          </CategoryContent>
        </TreeSubFolder>
      )
    }
    return (
      <TreeFolder key={IDENTITY_FOLDER_ID}>
        <FolderHeader
          isSelected={isIdentitySelected}
          folderColor={identityColor}
          onClick={() => handleFolderSelect(IDENTITY_FOLDER)}
          role='button'
          tabIndex={0}
          aria-label={ariaLabel}
          onKeyDown={onKeyDown}
        >
          <ExpandIcon />
          <FolderIconStyled color={identityColor}>
            <IdentityFolderIcon color={identityColor} />
          </FolderIconStyled>
          <FolderNameWithCount>
            <span>Identity documents</span>
            {displayCount && (
              <Tooltip title={tooltip}>
                <CategoryCount folderColor={identityColor} data-testid='identity-folder-count'>
                  {displayCount}
                </CategoryCount>
              </Tooltip>
            )}
            {lockIcon}
          </FolderNameWithCount>
        </FolderHeader>
      </TreeFolder>
    )
  }

  const identityCanView = identity.summary?.canView ?? identity.totalSummary?.canView
  const identityLocked = isIdentitySelected && identityCanView === false

  return (
    <UnifiedFilesWrapper>
      <MainContentPanel>
        <PanelHeaderWrapper>
          <TitleWrapper>
            <PageTitle>Documents</PageTitle>
            {documentCountSummary(filesCounts, hideDuplicates)?.shown > 0 && (
              <HeaderTotalCount>
                {documentCountSummary(filesCounts, hideDuplicates).text}
              </HeaderTotalCount>
            )}
            {uncategorisedCount > 0 && (
              <UncategorisedCounter>
                {uncategorisedCount} uncategorised
              </UncategorisedCounter>
            )}
          </TitleWrapper>

          <HeaderActions>
            {canEdit && bpUnits.length > 0 && (
              <HeaderBtn type='button' data-tour-id='files.file-to-units' onClick={() => setUnitFilingOpen(true)}>
                File documents to units
              </HeaderBtn>
            )}
            <Tooltip title='About these documents'>
              <InfoButton type='button' aria-label='About these documents' onClick={() => setInfoPanelOpen(true)}>
                <InfoOutlinedIcon sx={{ fontSize: 20 }} />
              </InfoButton>
            </Tooltip>
            <Tooltip title='Check files'>
              <InfoButton type='button' aria-label='Check files' onClick={() => setCheckFilesOpen(true)}>
                <FindInPageIcon sx={{ fontSize: 20 }} />
              </InfoButton>
            </Tooltip>
            {canEdit && activeTab !== 1 && (
              <UploadSlot>
                <AddMultipleFilesModal method="ai">
                  <UploadButton>
                    <span className='desktop-text'>UPLOAD</span>
                    <span className='mobile-icon'>
                      <UploadIcon sx={{ fontSize: 20 }} />
                    </span>
                  </UploadButton>
                </AddMultipleFilesModal>
              </UploadSlot>
            )}
          </HeaderActions>
          <FileInfoPanel
            isOpen={infoPanelOpen}
            onClose={() => setInfoPanelOpen(false)}
            bpId={bp?.id}
          />
          {unitFilingOpen && (
            <FileToUnitsWizard
              bpId={bp?.id}
              onClose={() => setUnitFilingOpen(false)}
            />
          )}
        </PanelHeaderWrapper>

        <ProcessingFilesPill type='building_passport' id={bp?.id} onCleared={refreshAfterProcessing} onRetry={canEdit && bp?.id ? () => window.open(`/files/upload/${bp.id}?method=ai`, '_blank') : undefined} />

        {!hideTabBar && (
        <TabsContainer>
          <StyledTabs value={activeTab} onChange={handleTabChange}>
            <StyledTab
              label={
                <TabContent isSelected={activeTab === 0} tabIndex={0}>
                  <CompanyLogo src={LogoShort} alt='Building Passport' />
                  <TabText isSelected={activeTab === 0}>Automatic</TabText>
                </TabContent>
              }
            />
            {showCustomTab && (
              <StyledTab
                data-tour-id='files.custom-tab'
                label={
                  <TabContent isSelected={activeTab === 1} tabIndex={1}>
                    <StyledPersonIcon />
                    <TabText isSelected={activeTab === 1}>Custom</TabText>
                  </TabContent>
                }
              />
            )}
            {showSecondaryTabs && (
              <StyledTab
                data-tour-id='files.needs-attention-tab'
                label={
                  <TabContent isSelected={activeTab === 2} tabIndex={2}>
                    <TroubleshootRoundedIcon style={{ fontSize: 18 }} />
                    <TabText isSelected={activeTab === 2}>Needs attention</TabText>
                    {needsAttentionCount > 0 && (
                      <Tooltip title={`${needsAttentionCount} document${needsAttentionCount === 1 ? ' needs' : 's need'} review`} arrow>
                        <AttentionCount aria-label={`${needsAttentionCount} documents need review`}>
                          {needsAttentionCount > 99 ? '99+' : needsAttentionCount}
                        </AttentionCount>
                      </Tooltip>
                    )}
                  </TabContent>
                }
              />
            )}
          </StyledTabs>
        </TabsContainer>
        )}

        {activeTab === 0 && (
          <TabContentWrapper activeTab={0}>
            {automaticTabContent ? automaticTabContent : (
              <>
            <SearchInputWrapper>
              <StreamlinedFilesSearch
                setIsSearching={setIsSearching}
                onSearchCriteria={setSearchCriteria}
                selectedFolder={selectedFolder}
                selectedCategory={selectedCategory}
                totalBpResults={totalBpResults}
                selectedViewableBy={selectedViewableBy}
                setSelectedViewableBy={setSelectedViewableBy}
                bp={bp}
              />
            </SearchInputWrapper>
            {isAggregate && sourceBpFilterOptions.length > 1 && (
              <BpFilterBar>
                <BpFilterIcon><ApartmentRoundedIcon style={{ fontSize: 20 }} /></BpFilterIcon>
                <BpFilterSelectWrap>
                  <SearchableSelect
                    value={selectedSourceBpId}
                    onChange={setSelectedSourceBpId}
                    options={sourceBpFilterOptions}
                    placeholder='All buildings'
                    searchPlaceholder='Find a building…'
                    clearable
                    clearLabel='All buildings'
                    ariaLabel='Filter documents by Building Passport'
                  />
                </BpFilterSelectWrap>
                {selectedSourceBpId && !isIdentitySelected && (
                  <BpFilterCount>
                    Showing {visibleFiles.length}{hasMore ? '+' : ''} document{visibleFiles.length === 1 && !hasMore ? '' : 's'} for this building
                  </BpFilterCount>
                )}
              </BpFilterBar>
            )}
            <DupToolbar>
              <HideDuplicatesControl
                checked={hideDuplicates}
                count={filesCounts?.duplicates || 0}
                hint='Hides duplicate copies here. Custom views are unaffected.'
                onChange={toggleHideDuplicates}
              />
            </DupToolbar>
            <GoogleDriveLayout>
              <Tooltip title='Show folders'>
                <FloatingMenuButton
                  type='button'
                  style={menuSlot.style}
                  onClick={() => setMobileDrawerOpen(true)}
                  aria-label='Show folders'
                  aria-expanded={mobileDrawerOpen}
                >
                  <AccountTreeIcon aria-hidden='true' />
                  <FloatingMenuLabel>Folders</FloatingMenuLabel>
                </FloatingMenuButton>
              </Tooltip>

              <MobileDrawerOverlay
                isOpen={mobileDrawerOpen}
                onClick={() => setMobileDrawerOpen(false)}
              />

              <MobileDrawer isOpen={mobileDrawerOpen}>
                <MobileDrawerHeader>
                  <MobileDrawerTitle>Folders</MobileDrawerTitle>
                  <MobileDrawerClose onClick={() => setMobileDrawerOpen(false)}>
                    <CloseIcon />
                  </MobileDrawerClose>
                </MobileDrawerHeader>
                <MobileDrawerContent>
                  <FolderTree>
                    {(() => {
                      const allFilesCount = (filesCounts?.total || 0) + identityTotalExtra

                      const matchingCount = searchMatchCountAllFiles(
                        searchCriteria?.isActive
                          ? (files?.length || 0)
                          : (filesCounts?.total || 0)
                      ) + (searchCriteria?.isActive ? identityMatchExtra : 0)

                      const displayCount = searchCriteria?.isActive
                        ? `(${matchingCount}/${allFilesCount})`
                        : `(${allFilesCount})`

                      const isSelected = selectedFolder?.id === 'all_files'
                      const allFilesColor = '#33becc'

                      return (
                        <TreeFolder key='all_files'>
                          <FolderHeader
                            data-tour-id='files.all-files'
                            isSelected={isSelected}
                            folderColor={allFilesColor}
                            onClick={() =>
                              handleFolderSelect({
                                id: 'all_files',
                                name: 'All Files',
                                files,
                                isVirtual: true
                              })
                            }
                          >
                            <ExpandIcon />
                            <FolderIconStyled color={allFilesColor}>
                              <FolderOpenIcon />
                            </FolderIconStyled>
                            <FolderNameWithCount>
                              <span>All Files</span>
                              <Tooltip
                                title={
                                  searchCriteria?.isActive
                                    ? `${matchingCount} matching / ${allFilesCount} total`
                                    : `${allFilesCount} total`
                                }
                              >
                                <CategoryCount folderColor={allFilesColor}>
                                  {displayCount}
                                </CategoryCount>
                              </Tooltip>
                              {allFilesLoaded && (
                                <Tooltip title='All files loaded and ready' placement='top'>
                                  <CheckCircleIcon style={{ fontSize: 12, color: '#4caf50', marginLeft: 4 }} />
                                </Tooltip>
                              )}
                            </FolderNameWithCount>
                          </FolderHeader>
                        </TreeFolder>
                      )
                    })()}

                    {renderIdentityTreeFolder()}

                    {(() => {
                      const uncategorisedFiles =
                        files?.filter(file => !file.fileType?.id) || []
                      const isSelected = selectedFolder?.id === 'uncategorised'
                      const uncategorisedColor = '#999999'

                      const uncategorisedCount =
                        allFilesLoaded && files && files.length > 0
                          ? uncategorisedFiles.length
                          : filesCounts?.byCategory?.['uncategorised'] || 0

                      if (
                        !searchCriteria?.isActive &&
                        filesCounts &&
                        uncategorisedCount === 0
                      ) {
                        return null
                      }

                      let matchingCount = searchMatchCountUncategorised(uncategorisedFiles.length)

                      if (searchCriteria?.isActive && matchingCount === 0) {
                        return null
                      }

                      const displayCount = searchCriteria?.isActive
                        ? (aggregateCountsActive
                            ? `(${matchingCount}/${uncategorisedCount})`
                            : `(${matchingCount})`)
                        : `(${uncategorisedCount})`

                      return (
                        <TreeFolder key='uncategorised'>
                          <FolderHeader
                            isSelected={isSelected}
                            folderColor={uncategorisedColor}
                            onClick={() =>
                              handleCategorySelect({
                                id: 'uncategorised',
                                name: 'Uncategorised Files'
                              })
                            }
                          >
                            <ExpandIcon />
                            <FolderIconStyled color={uncategorisedColor}>
                              <FolderIcon />
                            </FolderIconStyled>
                            <FolderNameWithCount>
                              <span>Uncategorised</span>
                              <Tooltip
                                title={
                                  searchCriteria?.isActive
                                    ? `${matchingCount} search results`
                                    : `${uncategorisedCount} total`
                                }
                              >
                                <CategoryCount folderColor={uncategorisedColor}>
                                  {displayCount}
                                </CategoryCount>
                              </Tooltip>
                              {allFilesLoaded && (
                                <Tooltip title={tickMeansMatches ? 'Has matching files' : 'Files loaded and ready'} placement='top'>
                                  <CheckCircleIcon style={{ fontSize: 12, color: '#4caf50', marginLeft: 4 }} />
                                </Tooltip>
                              )}
                            </FolderNameWithCount>
                          </FolderHeader>
                        </TreeFolder>
                      )
                    })()}

                    {folders &&
                      folders.map((folder, folderIndex) => {
                        const isExpanded = expandedFolders.has(folder.id)
                        const isSelected = selectedFolder?.id === folder.id
                        const folderCategories =
                          fileCategories?.filter(
                            cat => cat.folderId === folder.id
                          ) || []
                        const folderFileCount = getFolderFileCount(folder) + (isIdentityParent(folder) ? identityMatchExtra : 0)
                        const totalFolderFiles = getFolderTotalFileCount(folder) + (isIdentityParent(folder) ? identityTotalExtra : 0)
                        const folderColor =
                          colorPalette[folderIndex % colorPalette.length]

                        if (
                          filesCounts &&
                          !searchCriteria?.isActive &&
                          totalFolderFiles === 0 &&
                          !isIdentityHost(folder)
                        ) {
                          return null
                        }

                        if (searchCriteria?.isActive && folderFileCount === 0 && !isIdentityHost(folder)) {
                          return null
                        }

                        return (
                          <TreeFolder key={folder.id}>
                            <FolderHeader
                              isSelected={isSelected}
                              folderColor={folderColor}
                              onClick={() => handleFolderSelect(folder)}
                            >
                              <ExpandIcon>
                                {folderCategories.length > 0 &&
                                  (isExpanded ? (
                                    <ExpandMoreIcon />
                                  ) : (
                                    <ChevronRightIcon />
                                  ))}
                              </ExpandIcon>
                              <FolderIconStyled color={folderColor}>
                                {isExpanded ? <FolderOpenIcon /> : <FolderIcon />}
                              </FolderIconStyled>
                              <FolderNameWithCount>
                                <span>{folder.name}</span>
                                <Tooltip
                                  title={
                                    searchCriteria?.isActive
                                      ? (aggregateCountsActive
                                          ? `${folderFileCount} matching / ${totalFolderFiles} total`
                                          : `${folderFileCount} search results`)
                                      : `${totalFolderFiles} total files`
                                  }
                                >
                                  <CategoryCount folderColor={folderColor}>
                                    {searchCriteria?.isActive
                                      ? (aggregateCountsActive
                                          ? `(${folderFileCount}/${totalFolderFiles})`
                                          : `(${folderFileCount})`)
                                      : `(${totalFolderFiles})`}
                                  </CategoryCount>
                                </Tooltip>
                                {folderShowsTick(folder) && (
                                  <Tooltip title={tickMeansMatches ? 'Has matching files' : 'Files loaded and ready'} placement='top'>
                                    <CheckCircleIcon style={{ fontSize: 12, color: '#4caf50', marginLeft: 4 }} />
                                  </Tooltip>
                                )}
                                {isOwner && (
                                  <FolderPermissionsIndicator
                                    userCount={folderUserCounts?.folders?.[folder.id] || 0}
                                    folderId={folder.id}
                                    folderType="folder"
                                    isOwner={true}
                                    onPermissionsUpdated={fetchFolderUserCounts}
                                  />
                                )}
                              </FolderNameWithCount>
                            </FolderHeader>

                            {isExpanded && isIdentityHost(folder) && renderIdentityTreeFolder({ nested: true, folderColor })}
                            {isExpanded &&
                              folderCategories.map(category => {
                                const categoryIsLoaded =
                                  allFilesLoaded ||
                                  loadedFolders.has(folder.id) ||
                                  loadedCategories.has(category.id)

                                // Total: ALWAYS from cached counts (whole-BP truth)
                                let totalCount = 0
                                if (filesCounts?.byCategory) {
                                  const normalizedId = category.id?.toLowerCase()
                                  totalCount = filesCounts.byCategory[normalizedId] || 0
                                } else if (categoryIsLoaded && allFiles && allFiles.length > 0) {
                                  totalCount = allFiles.filter(
                                    file => pathOr('', ['fileType', 'fileCategoryId'], file) === category.id
                                  ).length || 0
                                }

                                // Matching: filter active result set against this category
                                let matchingCount = totalCount
                                if (aggregateCountsActive) {
                                  matchingCount = searchMatchCountForCategory(category.id, matchingCount)
                                } else if (searchCriteria?.isActive && files) {
                                  matchingCount = files.filter(file =>
                                    pathOr('', ['fileType', 'fileCategoryId'], file) === category.id
                                  ).length
                                }

                                // Hide categories with no matches (searching) or no files (not searching)
                                if (
                                  (searchCriteria?.isActive && matchingCount === 0) ||
                                  (!searchCriteria?.isActive && totalCount === 0)
                                ) {
                                  return null
                                }

                                const displayCount = searchCriteria?.isActive
                                  ? `(${matchingCount}/${totalCount})`
                                  : `(${totalCount})`

                                return (
                                  <TreeSubFolder
                                    key={category.id}
                                    isSelected={
                                      selectedCategory?.id === category.id
                                    }
                                    folderColor={folderColor}
                                    onClick={e => {
                                      e.stopPropagation()
                                      handleCategorySelect(category)
                                    }}
                                  >
                                    <CategoryIcon folderColor={folderColor}>
                                      <ArticleIcon />
                                    </CategoryIcon>
                                    <CategoryContent>
                                      <CategoryName>{category.name}</CategoryName>
                                      <Tooltip
                                        title={
                                          searchCriteria?.isActive
                                            ? `${matchingCount} matching / ${totalCount} total`
                                            : `${totalCount} total`
                                        }
                                      >
                                        <CategoryFileCount
                                          folderColor={folderColor}
                                        >
                                          {displayCount}
                                        </CategoryFileCount>
                                      </Tooltip>
                                      {categoryShowsTick(category) && (
                                        <Tooltip title={tickMeansMatches ? 'Has matching files' : 'Files loaded and ready'} placement='top'>
                                          <CheckCircleIcon style={{ fontSize: 12, color: '#4caf50', marginLeft: 4 }} />
                                        </Tooltip>
                                      )}
                                      {isOwner && (
                                        <FolderPermissionsIndicator
                                          userCount={folderUserCounts?.categories?.[category.id] || 0}
                                          folderId={category.id}
                                          folderType="category"
                                          isOwner={true}
                                          onPermissionsUpdated={fetchFolderUserCounts}
                                        />
                                      )}
                                    </CategoryContent>
                                  </TreeSubFolder>
                                )
                              })}
                          </TreeFolder>
                        )
                      })}
                  </FolderTree>
                </MobileDrawerContent>
              </MobileDrawer>

              <TreeNavigationPanel>
                <TreeHeader>Folders</TreeHeader>
                <FolderTree>
                  {/* Virtual folder: All Files */}
                  {(() => {
                    // ALWAYS use API counts for All Files (not files.length from lazy loading)
                    const allFilesCount = (filesCounts?.total || 0) + identityTotalExtra

                    // When search is active, files array is already Bedrock KB results - no need to filter
                    const matchingCount = searchMatchCountAllFiles(
                      searchCriteria?.isActive
                        ? (files?.length || 0)
                        : (filesCounts?.total || 0)
                    ) + (searchCriteria?.isActive ? identityMatchExtra : 0)

                    const displayCount = searchCriteria?.isActive
                      ? `(${matchingCount}/${allFilesCount})`
                      : `(${allFilesCount})`

                    const isSelected = selectedFolder?.id === 'all_files'
                    const allFilesColor = '#33becc'

                    return (
                      <TreeFolder key='all_files'>
                        <FolderHeader
                          data-tour-id='files.all-files'
                          isSelected={isSelected}
                          folderColor={allFilesColor}
                          onClick={() =>
                            handleFolderSelect({
                              id: 'all_files',
                              name: 'All Files',
                              files,
                              isVirtual: true
                            })
                          }
                        >
                          <ExpandIcon />
                          <FolderIconStyled color={allFilesColor}>
                            <FolderOpenIcon />
                          </FolderIconStyled>
                          <FolderNameWithCount>
                            <span>All Files</span>
                            <Tooltip
                              title={
                                searchCriteria?.isActive
                                  ? `${matchingCount} matching / ${allFilesCount} total`
                                  : `${allFilesCount} total`
                              }
                            >
                              <CategoryCount folderColor={allFilesColor}>
                                {displayCount}
                              </CategoryCount>
                            </Tooltip>
                            {allFilesLoaded && (
                              <Tooltip title='All files loaded and ready' placement='top'>
                                <CheckCircleIcon style={{ fontSize: 12, color: '#4caf50', marginLeft: 4 }} />
                              </Tooltip>
                            )}
                          </FolderNameWithCount>
                        </FolderHeader>
                      </TreeFolder>
                    )
                  })()}

                  {renderIdentityTreeFolder()}

                  {/* Add Uncategorised as the first folder */}
                  {(() => {
                    const uncategorisedFiles =
                      files?.filter(file => !file.fileType?.id) || []
                    const isSelected = selectedFolder?.id === 'uncategorised'
                    const uncategorisedColor = '#999999'

                    // Get count from API if files not loaded, otherwise from loaded files
                    // Only count from files if ALL files loaded (not lazy loaded partial data)
                    const uncategorisedCount =
                      allFilesLoaded && files && files.length > 0
                        ? uncategorisedFiles.length
                        : filesCounts?.byCategory?.['uncategorised'] || 0

                    // Only hide if we have filesCounts and count is 0
                    if (
                      !searchCriteria?.isActive &&
                      filesCounts &&
                      uncategorisedCount === 0
                    ) {
                      return null
                    }

                    // When search is active, files array already contains filtered results from Bedrock KB
                    // No need for additional local filtering
                    let matchingCount = searchMatchCountUncategorised(uncategorisedFiles.length)

                    if (searchCriteria?.isActive && matchingCount === 0) {
                      return null
                    }

                    const displayCount = searchCriteria?.isActive
                      ? (aggregateCountsActive
                          ? `(${matchingCount}/${uncategorisedCount})`
                          : `(${matchingCount})`)
                      : `(${uncategorisedCount})`

                    return (
                      <TreeFolder key='uncategorised'>
                        <FolderHeader
                          isSelected={isSelected}
                          folderColor={uncategorisedColor}
                          onClick={() =>
                            handleCategorySelect({
                              id: 'uncategorised',
                              name: 'Uncategorised Files'
                              // Don't pass files - let them be filtered dynamically
                            })
                          }
                        >
                          <ExpandIcon />
                          <FolderIconStyled color={uncategorisedColor}>
                            <FolderIcon />
                          </FolderIconStyled>
                          <FolderNameWithCount>
                            <span>Uncategorised</span>
                            <Tooltip
                              title={
                                searchCriteria?.isActive
                                  ? `${matchingCount} search results`
                                  : `${uncategorisedCount} total`
                              }
                            >
                              <CategoryCount folderColor={uncategorisedColor}>
                                {displayCount}
                              </CategoryCount>
                            </Tooltip>
                            {allFilesLoaded && (
                              <Tooltip title={tickMeansMatches ? 'Has matching files' : 'Files loaded and ready'} placement='top'>
                                <CheckCircleIcon style={{ fontSize: 12, color: '#4caf50', marginLeft: 4 }} />
                              </Tooltip>
                            )}
                          </FolderNameWithCount>
                        </FolderHeader>
                      </TreeFolder>
                    )
                  })()}

                  {folders &&
                    folders.map((folder, folderIndex) => {
                      const isExpanded = expandedFolders.has(folder.id)
                      const isSelected = selectedFolder?.id === folder.id
                      const folderCategories =
                        fileCategories?.filter(
                          cat => cat.folderId === folder.id
                        ) || []
                      const folderFileCount = getFolderFileCount(folder) + (isIdentityParent(folder) ? identityMatchExtra : 0)
                      const totalFolderFiles = getFolderTotalFileCount(folder) + (isIdentityParent(folder) ? identityTotalExtra : 0)
                      const folderColor =
                        colorPalette[folderIndex % colorPalette.length]

                      // Only hide folders if we KNOW they have 0 files (filesCounts loaded)
                      // Don't hide if filesCounts is still loading
                      if (
                        filesCounts &&
                        !searchCriteria?.isActive &&
                        totalFolderFiles === 0 &&
                        !isIdentityHost(folder)
                      ) {
                        return null
                      }

                      // Hide during search if no matching files
                      if (searchCriteria?.isActive && folderFileCount === 0 && !isIdentityHost(folder)) {
                        return null
                      }

                      return (
                        <TreeFolder key={folder.id}>
                          <FolderHeader
                            isSelected={isSelected}
                            folderColor={folderColor}
                            onClick={() => handleFolderSelect(folder)}
                          >
                            <ExpandIcon>
                              {folderCategories.length > 0 &&
                                (isExpanded ? (
                                  <ExpandMoreIcon />
                                ) : (
                                  <ChevronRightIcon />
                                ))}
                            </ExpandIcon>
                            <FolderIconStyled color={folderColor}>
                              {isExpanded ? <FolderOpenIcon /> : <FolderIcon />}
                            </FolderIconStyled>
                            <FolderNameWithCount>
                              <span>{folder.name}</span>
                              <Tooltip
                                title={
                                  searchCriteria?.isActive
                                    ? (aggregateCountsActive
                                        ? `${folderFileCount} matching / ${totalFolderFiles} total`
                                        : `${folderFileCount} search results`)
                                    : `${totalFolderFiles} total files`
                                }
                              >
                                <CategoryCount folderColor={folderColor}>
                                  {searchCriteria?.isActive
                                    ? (aggregateCountsActive
                                        ? `(${folderFileCount}/${totalFolderFiles})`
                                        : `(${folderFileCount})`)
                                    : `(${totalFolderFiles})`}
                                </CategoryCount>
                              </Tooltip>
                              {folderShowsTick(folder) && (
                                <Tooltip title={tickMeansMatches ? 'Has matching files' : 'Files loaded and ready'} placement='top'>
                                  <CheckCircleIcon style={{ fontSize: 12, color: '#4caf50', marginLeft: 4 }} />
                                </Tooltip>
                              )}
                              {isOwner && (
                                <FolderPermissionsIndicator
                                  userCount={folderUserCounts?.folders?.[folder.id] || 0}
                                  folderId={folder.id}
                                  folderType="folder"
                                  isOwner={true}
                                  onPermissionsUpdated={fetchFolderUserCounts}
                                />
                              )}
                            </FolderNameWithCount>
                          </FolderHeader>

                          {/* Render categories within folder */}
                          {isExpanded && isIdentityHost(folder) && renderIdentityTreeFolder({ nested: true, folderColor })}
                          {isExpanded &&
                            folderCategories.map(category => {
                              const categoryIsLoaded =
                                allFilesLoaded ||
                                loadedFolders.has(folder.id) ||
                                loadedCategories.has(category.id)

                              // Total: ALWAYS from cached counts (correct for the whole BP, regardless of load state)
                              let totalCount = 0
                              if (filesCounts?.byCategory) {
                                const normalizedId = category.id?.toLowerCase()
                                totalCount = filesCounts.byCategory[normalizedId] || 0
                              } else if (categoryIsLoaded && allFiles && allFiles.length > 0) {
                                totalCount = allFiles.filter(
                                  file => pathOr('', ['fileType', 'fileCategoryId'], file) === category.id
                                ).length || 0
                              }

                              // Matching: filter the active result set against this category
                              let matchingCount = totalCount
                              if (aggregateCountsActive) {
                                matchingCount = searchMatchCountForCategory(category.id, matchingCount)
                              } else if (searchCriteria?.isActive && files) {
                                matchingCount = files.filter(file =>
                                  pathOr('', ['fileType', 'fileCategoryId'], file) === category.id
                                ).length
                              }

                              // Hide categories with no matches (searching) or no files (not searching)
                              if (
                                (searchCriteria?.isActive && matchingCount === 0) ||
                                (!searchCriteria?.isActive && totalCount === 0)
                              ) {
                                return null
                              }

                              const displayCount = searchCriteria?.isActive
                                ? `(${matchingCount}/${totalCount})`
                                : `(${totalCount})`

                              return (
                                <TreeSubFolder
                                  key={category.id}
                                  isSelected={
                                    selectedCategory?.id === category.id
                                  }
                                  folderColor={folderColor}
                                  onClick={e => {
                                    e.stopPropagation()
                                    handleCategorySelect(category)
                                  }}
                                >
                                  <CategoryIcon folderColor={folderColor}>
                                    <ArticleIcon />
                                  </CategoryIcon>
                                  <CategoryContent>
                                    <CategoryName>{category.name}</CategoryName>
                                    <Tooltip
                                      title={
                                        searchCriteria?.isActive
                                          ? `${matchingCount} matching / ${totalCount} total`
                                          : `${totalCount} total`
                                      }
                                    >
                                      <CategoryFileCount
                                        folderColor={folderColor}
                                      >
                                        {displayCount}
                                      </CategoryFileCount>
                                    </Tooltip>
                                    {categoryShowsTick(category) && (
                                      <Tooltip title={tickMeansMatches ? 'Has matching files' : 'Files loaded and ready'} placement='top'>
                                        <CheckCircleIcon style={{ fontSize: 12, color: '#4caf50', marginLeft: 4 }} />
                                      </Tooltip>
                                    )}
                                    {isOwner && (
                                      <FolderPermissionsIndicator
                                        userCount={folderUserCounts?.categories?.[category.id] || 0}
                                        folderId={category.id}
                                        folderType="category"
                                        isOwner={true}
                                        onPermissionsUpdated={fetchFolderUserCounts}
                                      />
                                    )}
                                  </CategoryContent>
                                </TreeSubFolder>
                              )
                            })}
                        </TreeFolder>
                      )
                    })}
                </FolderTree>
              </TreeNavigationPanel>
              <FolderGridPanel>
                {(() => {
                  if (isIdentitySelected) return null
                  const isLoading = (
                    isLoadingBpFiles ||
                    isLoadingAllFiles ||
                    (selectedFolder && selectedFolder.id !== 'all_files' && selectedFolder.id !== 'uncategorised' && loadingFolders.has(selectedFolder.id)) ||
                    (selectedFolder && selectedFolder.id === 'all_files' && loadingFolders.has('all_files')) ||
                    (selectedFolder && selectedFolder.id === 'uncategorised' && !allFilesLoaded && !loadedCategories.has('uncategorised')) ||
                    (selectedCategory && (loadingCategories.has(selectedCategory.id) || loadingFolders.has(selectedCategory.id))) ||
                    (!selectedFolder && !selectedCategory && loadingFolders.size > 0)
                  )
                  if (!isLoading) return null

                  const totalFiles = aggregateCountsActive
                    ? computedSelectionTotal
                    : !filesCounts ? 0 :
                    (isLoadingBpFiles || isLoadingAllFiles) ? (filesCounts.total || 0) :
                    (selectedFolder && selectedFolder.id === 'all_files') ? (filesCounts.total || 0) :
                    (selectedFolder && selectedFolder.id !== 'all_files' && selectedFolder.id !== 'uncategorised') ?
                      fileCategories.filter(cat => cat.folderId === selectedFolder.id).map(cat => cat.id).reduce((sum, catId) => sum + (filesCounts.byCategory?.[catId.toLowerCase()] || 0), 0) :
                    selectedCategory ? (filesCounts.byCategory?.[selectedCategory.id.toLowerCase()] || 0) : 0

                  return (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 16px',
                      background: 'linear-gradient(90deg, #f5f5f5 25%, #e8e8e8 50%, #f5f5f5 75%)',
                      backgroundSize: '200% 100%',
                      animation: 'shimmer 1.5s infinite linear',
                      borderRadius: 6,
                      margin: '8px 12px',
                      fontSize: 13,
                      color: '#666',
                      fontWeight: 500
                    }}>
                      <CircularProgress size={14} style={{ color: '#999' }} />
                      {(() => {
                        const key =
                          isLoadingBpFiles ? 'bp' :
                          isLoadingAllFiles ? 'all' :
                          selectedFolder ? selectedFolder.id :
                          selectedCategory ? selectedCategory.id : null
                        const loaded = key ? (loadingProgress[key]?.loaded || 0) : 0
                        if (loaded > 0 && totalFiles > 0) {
                          return `Loading files — ${Math.min(loaded, totalFiles)} of ${totalFiles}`
                        }
                        return totalFiles > 0 ? `Loading files — ${totalFiles} ${totalFiles === 1 ? 'file' : 'files'}` : 'Loading files...'
                      })()}
                      <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
                    </div>
                  )
                })()}
                  {identityLocked && (
                    <IdentityLockedPanel
                      count={identitySearching ? null : (identity.summary?.count ?? identity.totalSummary?.count ?? 0)}
                      buildingCount={identity.summary?.buildingCount ?? identity.totalSummary?.buildingCount ?? 0}
                      isAggregate={isAggregate}
                    />
                  )}
                  {isIdentitySelected && identityCanView === true && <IdentityAccessNote />}
                  {isIdentitySelected && !identityLocked ? (
                  <FileCategories
                    setRecentFilesVisible={() => {}}
                    showOnlyMyFiles={showOnlyMyFiles}
                    setIsSearching={setIsSearching}
                    isSearching={isSearching}
                    isLoadingFiles={!identity.isLoaded && identity.files.length === 0}
                    selectedFolder={selectedFolder}
                    selectedCategory={null}
                    searchCriteria={searchCriteria}
                    totalBpResults={totalBpResults}
                    setSearchCriteria={setSearchCriteria}
                    files={identity.files}
                    hasMore={identity.hasMore}
                    loadingMore={identity.loading && identity.files.length > 0}
                    onLoadMore={identity.loadMore}
                    total={identity.summary?.count ?? identity.files.length}
                    onFolderSelect={handleFolderSelect}
                    onCategorySelect={handleCategorySelect}
                    selectedViewableBy={selectedViewableBy}
                    searchMode={searchMode}
                    onOpenEditModal={handleEditModalOpen}
                    onOpenMobileDrawer={() => setMobileDrawerOpen(true)}
                    context={context}
                  />
                  ) : !isIdentitySelected && (
                  <FileCategories
                    setRecentFilesVisible={() => {}}
                    showOnlyMyFiles={showOnlyMyFiles}
                    setIsSearching={setIsSearching}
                    isSearching={isSearching}
                    isLoadingFiles={
                      isLoadingBpFiles ||
                      isLoadingAllFiles ||
                      (selectedFolder && selectedFolder.id !== 'all_files' && selectedFolder.id !== 'uncategorised' && loadingFolders.has(selectedFolder.id)) ||
                      (selectedFolder && selectedFolder.id === 'all_files' && loadingFolders.has('all_files')) ||
                      (selectedFolder && selectedFolder.id === 'uncategorised' && !allFilesLoaded && !loadedCategories.has('uncategorised')) ||
                      (selectedCategory && (loadingCategories.has(selectedCategory.id) || loadingFolders.has(selectedCategory.id))) ||
                      (!selectedFolder && !selectedCategory && loadingFolders.size > 0)
                    }
                    selectedFolder={selectedFolder}
                    selectedCategory={selectedCategory}
                    searchCriteria={searchCriteria}
                    totalBpResults={totalBpResults}
                    setSearchCriteria={setSearchCriteria}
                    files={visibleFiles}
                    extraFiles={identityInAllFiles ? identity.files : undefined}
                    hasMore={hasMore || (identityInAllFiles && identity.hasMore)}
                    loadingMore={loadingMore || (identityInAllFiles && identity.loading && identity.files.length > 0)}
                    onLoadMore={identityInAllFiles && !hasMore ? identity.loadMore : onLoadMore}
                    total={identityInAllFiles && typeof total === 'number' ? total + identityMatchExtra : total}
                    onFolderSelect={handleFolderSelect}
                    onCategorySelect={handleCategorySelect}
                    selectedViewableBy={selectedViewableBy}
                    searchMode={searchMode}
                    onRemoveFilter={(type, id) => {
                      // Handle filter removal
                      if (type === 'query') {
                        setSearchQuery('')
                      } else if (type === 'uploader') {
                        setSelectedUploaders(prev =>
                          prev.filter(uid => uid !== id)
                        )
                      } else if (type === 'fileType') {
                        setSelectedFileTypes(prev =>
                          prev.filter(tid => tid !== id)
                        )
                      } else if (type === 'tag') {
                        setSelectedFileTags(prev =>
                          prev.filter(tid => tid !== id)
                        )
                      }
                    }}
                    onClearAllFilters={() => {
                      setSearchQuery('')
                      setSelectedUploaders([])
                      setSelectedFileTypes([])
                      setSelectedFileTags([])
                      setSelectedViewableBy([])
                    }}
                    onOpenEditModal={handleEditModalOpen}
                    onOpenMobileDrawer={() => setMobileDrawerOpen(true)}
                    context={context}
                  />
                  )}
              </FolderGridPanel>
            </GoogleDriveLayout>
              </>
            )}
          </TabContentWrapper>
        )}

        {activeTab === 1 && showCustomTab && (
          <TabContentWrapper activeTab={1}>
            {customViewContent ? customViewContent : (
              <>
                {customBuildings?.length > 0 && (
                  <CustomBuildingBar>
                    <CustomBuildingBarIcon>
                      <ApartmentRoundedIcon style={{ fontSize: 22 }} />
                    </CustomBuildingBarIcon>
                    <CustomBuildingBarText>
                      <CustomBuildingBarLabel>Viewing documents for</CustomBuildingBarLabel>
                      <CustomBuildingSelectWrap>
                        <SearchableSelect
                          value={selectedCustomBpId}
                          onChange={setSelectedCustomBpId}
                          options={customBuildings.map(b => ({
                            value: b.id,
                            label: b.name,
                            sublabel: b.address || undefined,
                            group: b.estateName || undefined
                          }))}
                          placeholder='Select a building'
                        />
                      </CustomBuildingSelectWrap>
                    </CustomBuildingBarText>
                    <CustomBuildingBarCount>
                      {customBuildings.length} building{customBuildings.length === 1 ? '' : 's'}
                    </CustomBuildingBarCount>
                  </CustomBuildingBar>
                )}
                {customBuildings?.length > 0 && customBpError ? (
                  <CustomBpNotice>
                    <CustomBpNoticeIcon $forbidden={customBpError === 'forbidden'}>
                      {customBpError === 'forbidden' ? <LockOutlinedIcon /> : <InfoOutlinedIcon />}
                    </CustomBpNoticeIcon>
                    <CustomBpNoticeTitle>
                      {customBpError === 'forbidden'
                        ? "You don't have access to this building's documents"
                        : "Couldn't load this building"}
                    </CustomBpNoticeTitle>
                    <CustomBpNoticeText>
                      {customBpError === 'forbidden'
                        ? 'You can still see its files in the Automatic view, but you don’t have permission to open its custom folders. Choose another building above.'
                        : 'Something went wrong loading this building’s documents. Please choose another building above, or try again.'}
                    </CustomBpNoticeText>
                  </CustomBpNotice>
                ) : customBuildings?.length > 0 && (customBpLoading || selectedBpFromStore?.id !== selectedCustomBpId) ? (
                  <CustomBpSkeleton>
                    <CustomBpSkeletonRail>
                      {[70, 55, 62, 48, 58].map((w, i) => (
                        <CustomBpSkeletonLine key={i} style={{ width: `${w}%` }} />
                      ))}
                    </CustomBpSkeletonRail>
                    <CustomBpSkeletonGrid>
                      {Array.from({ length: 6 }).map((_, i) => (
                        <CustomBpSkeletonCard key={i} />
                      ))}
                    </CustomBpSkeletonGrid>
                  </CustomBpSkeleton>
                ) : (
                <>
                <SearchInputWrapper>
                  <StreamlinedFilesSearch
                    setIsSearching={setIsSearching}
                    onSearchCriteria={setSearchCriteria}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    searchMode={searchMode}
                    setSearchMode={setSearchMode}
                    selectedUploaders={selectedUploaders}
                    setSelectedUploaders={setSelectedUploaders}
                    selectedFileTypes={selectedFileTypes}
                    setSelectedFileTypes={setSelectedFileTypes}
                    selectedFileTags={selectedFileTags}
                    setSelectedFileTags={setSelectedFileTags}
                    selectedViewableBy={selectedViewableBy}
                    setSelectedViewableBy={setSelectedViewableBy}
                    customViewId={activeCustomViewId}
                  />
                </SearchInputWrapper>
                <CustomViews
                  key={customBuildings?.length ? bp?.id : undefined}
                  onViewChange={setActiveCustomViewId}
                  onFolderSelect={folder => setSelectedFolder(folder)}
                  selectedFolder={selectedFolder}
                  canEdit={canEdit}
                  searchCriteria={searchCriteria}
                  showOnlyMyFiles={showOnlyMyFiles}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  searchMode={searchMode}
                  selectedUploaders={selectedUploaders}
                  setSelectedUploaders={setSelectedUploaders}
                  selectedFileTypes={selectedFileTypes}
                  setSelectedFileTypes={setSelectedFileTypes}
                  selectedFileTags={selectedFileTags}
                  setSelectedFileTags={setSelectedFileTags}
                  selectedViewableBy={selectedViewableBy}
                  setSelectedViewableBy={setSelectedViewableBy}
                  onRemoveFilter={(type, id) => {
                    if (type === 'query') {
                      setSearchQuery('')
                    } else if (type === 'uploader') {
                      setSelectedUploaders(prev => prev.filter(uid => uid !== id))
                    } else if (type === 'fileType') {
                      setSelectedFileTypes(prev => prev.filter(tid => tid !== id))
                    } else if (type === 'tag') {
                      setSelectedFileTags(prev => prev.filter(tid => tid !== id))
                    }
                  }}
                  onClearAllFilters={() => {
                    setSearchQuery('')
                    setSelectedUploaders([])
                    setSelectedFileTypes([])
                    setSelectedFileTags([])
                    setSelectedViewableBy([])
                  }}
                />
                </>
                )}
              </>
            )}
          </TabContentWrapper>
        )}

        {activeTab === 2 && showSecondaryTabs && (
          <TabContentWrapper activeTab={2}>
            <FileAnalysisPanel bp={bp} canEdit={canEdit} />
          </TabContentWrapper>
        )}

        {activeTab === 3 && (
          <TabContentWrapper activeTab={3}>
            <SideBySideContainer>
              <SideBySidePanel>
                <SearchInputWrapper>
                  <StreamlinedFilesSearch
                    setIsSearching={() => {}}
                    onSearchCriteria={setSearchCriteria}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    selectedUploaders={selectedUploaders}
                    setSelectedUploaders={setSelectedUploaders}
                    selectedFileTypes={selectedFileTypes}
                    setSelectedFileTypes={setSelectedFileTypes}
                    selectedFileTags={selectedFileTags}
                    setSelectedFileTags={setSelectedFileTags}
                  />
                </SearchInputWrapper>
                <SideBySideLayout>
                  <SideBySideTreePanel>
                    <TreeHeader>Folders</TreeHeader>
                    <FolderTree>
                      {/* Add Uncategorised as the first folder */}
                      {(() => {
                        const uncategorisedFiles =
                          files?.filter(file => !file.fileType?.id) || []
                        const isSelected =
                          selectedCategory?.id === 'uncategorised'
                        const uncategorisedColor = '#999999'

                        if (uncategorisedFiles.length === 0) return null

                        // When search is active, files array already contains filtered results from Bedrock KB
                        // No need for additional local filtering
                        let matchingCount = uncategorisedFiles.length

                        if (
                          (searchCriteria?.isActive && matchingCount === 0) ||
                          (!searchCriteria?.isActive && uncategorisedFiles.length === 0)
                        ) {
                          return null
                        }

                        const displayCount = searchCriteria?.isActive
                          ? `(${matchingCount}/${uncategorisedFiles.length})`
                          : `(${uncategorisedFiles.length})`

                        return (
                          <TreeFolder key='uncategorised'>
                            <FolderHeader
                              isSelected={isSelected}
                              folderColor={uncategorisedColor}
                              onClick={() =>
                                handleCategorySelect({
                                  id: 'uncategorised',
                                  name: 'Uncategorised Files',
                                  files: uncategorisedFiles
                                })
                              }
                            >
                              <ExpandIcon />
                              <FolderIconStyled color={uncategorisedColor}>
                                <FolderIcon />
                              </FolderIconStyled>
                              <FolderNameWithCount>
                                <span>Uncategorised</span>
                                <Tooltip
                                  title={
                                    searchCriteria?.isActive
                                      ? `${matchingCount} matching / ${uncategorisedFiles.length} total`
                                      : `${uncategorisedFiles.length} total`
                                  }
                                >
                                  <CategoryCount
                                    folderColor={uncategorisedColor}
                                  >
                                    {displayCount}
                                  </CategoryCount>
                                </Tooltip>
                              </FolderNameWithCount>
                            </FolderHeader>
                          </TreeFolder>
                        )
                      })()}

                      {folders &&
                        folders.map((folder, folderIndex) => {
                          const isExpanded = expandedFolders.has(folder.id)
                          const isSelected = selectedFolder?.id === folder.id
                          const folderCategories =
                            fileCategories?.filter(
                              cat => cat.folderId === folder.id
                            ) || []
                          const folderCategoryIds = folderCategories.map(
                            cat => cat.id
                          )
                          const folderFileCount = files.filter(file =>
                            folderCategoryIds.includes(
                              pathOr('', ['fileType', 'fileCategoryId'], file)
                            )
                          ).length
                          const folderColor =
                            colorPalette[folderIndex % colorPalette.length]

                          return (
                            <TreeFolder key={folder.id}>
                              <FolderHeader
                                isSelected={isSelected}
                                folderColor={folderColor}
                                onClick={() => handleFolderSelect(folder)}
                              >
                                <ExpandIcon>
                                  {folderCategories.length > 0 &&
                                    (isExpanded ? (
                                      <ExpandMoreIcon />
                                    ) : (
                                      <ChevronRightIcon />
                                    ))}
                                </ExpandIcon>
                                <FolderIconStyled color={folderColor}>
                                  {isExpanded ? (
                                    <FolderOpenIcon />
                                  ) : (
                                    <FolderIcon />
                                  )}
                                </FolderIconStyled>
                                <FolderNameWithCount>
                                  <span>{folder.name}</span>
                                  <CategoryCount folderColor={folderColor}>
                                    ({folderFileCount})
                                  </CategoryCount>
                                  {folderShowsTick(folder) && (
                                    <Tooltip title="Files loaded and ready" placement="top">
                                      <CheckCircleIcon style={{ fontSize: 12, color: '#4caf50', marginLeft: 4 }} />
                                    </Tooltip>
                                  )}
                                </FolderNameWithCount>
                              </FolderHeader>
                              {isExpanded &&
                                folderCategories.map(category => {
                                  const categoryFileCount =
                                    files?.filter(
                                      file =>
                                        pathOr(
                                          '',
                                          ['fileType', 'fileCategoryId'],
                                          file
                                        ) === category.id
                                    ).length || 0

                                  return (
                                    <TreeSubFolder
                                      key={category.id}
                                      isSelected={
                                        selectedCategory?.id === category.id
                                      }
                                      folderColor={folderColor}
                                      onClick={e => {
                                        e.stopPropagation()
                                        handleCategorySelect(category)
                                      }}
                                    >
                                      <CategoryIcon folderColor={folderColor}>
                                        <ArticleIcon />
                                      </CategoryIcon>
                                      <CategoryContent>
                                        <CategoryName>
                                          {category.name}
                                        </CategoryName>
                                        <CategoryFileCount
                                          folderColor={folderColor}
                                        >
                                          ({categoryFileCount})
                                        </CategoryFileCount>
                                        {categoryShowsTick(category) && (
                                          <Tooltip title="Files loaded and ready" placement="top">
                                            <CheckCircleIcon style={{ fontSize: 12, color: '#4caf50', marginLeft: 4 }} />
                                          </Tooltip>
                                        )}
                                      </CategoryContent>
                                    </TreeSubFolder>
                                  )
                                })}
                            </TreeFolder>
                          )
                        })}
                    </FolderTree>
                  </SideBySideTreePanel>
                  <SideBySideFilesPanel>
                    <FileCategories
                      setRecentFilesVisible={() => {}}
                      showOnlyMyFiles={showOnlyMyFiles}
                      setIsSearching={setIsSearching}
                      isSearching={isSearching}
                      selectedFolder={selectedFolder}
                      selectedCategory={selectedCategory}
                      searchCriteria={searchCriteria}
                      totalBpResults={totalBpResults}
                      setSearchCriteria={setSearchCriteria}
                      searchMode={searchMode}
                      onRemoveFilter={(type, id) => {
                        if (type === 'query') {
                          setSearchQuery('')
                        } else if (type === 'uploader') {
                          setSelectedUploaders(prev =>
                            prev.filter(uid => uid !== id)
                          )
                        } else if (type === 'fileType') {
                          setSelectedFileTypes(prev =>
                            prev.filter(tid => tid !== id)
                          )
                        } else if (type === 'tag') {
                          setSelectedFileTags(prev =>
                            prev.filter(tid => tid !== id)
                          )
                        }
                      }}
                      onClearAllFilters={() => {
                        setSearchQuery('')
                        setSelectedUploaders([])
                        setSelectedFileTypes([])
                        setSelectedFileTags([])
                      }}
                    />
                  </SideBySideFilesPanel>
                </SideBySideLayout>
              </SideBySidePanel>
              <SideBySidePanel>
                <SearchInputWrapper>
                  <StreamlinedFilesSearch
                    setIsSearching={() => {}}
                    onSearchCriteria={setSearchCriteria}
                  />
                </SearchInputWrapper>
                <EmptyPanel>
                  <EmptyPanelMessage>
                    Select a folder to view its contents here
                  </EmptyPanelMessage>
                </EmptyPanel>
              </SideBySidePanel>
            </SideBySideContainer>
          </TabContentWrapper>
        )}
      </MainContentPanel>

      <ShareBulkFilesModal
        open={bulkShareModalOpen}
        onClose={() => setBulkShareModalOpen(false)}
        selectedFiles={selectedFiles}
      />
      <UnsharedBulkFilesModal
        open={bulkUnsharedModalOpen}
        onClose={() => setBulkUnsharedModalOpen(false)}
        selectedFiles={selectedFiles}
      />
      <UnshareAllForEmailModal
        open={unshareAllModalOpen}
        onClose={() => setUnshareAllModalOpen(false)}
        bpId={bp?.id}
      />
      <BulkEditFileModal
        open={bulkEditModalOpen}
        onClose={() => setBulkEditModalOpen(false)}
        selectedFiles={selectedFiles}
      />
      <ReclassifyFileModal
        open={reclassifyModalOpen}
        onClose={() => setReclassifyModalOpen(false)}
        file={selectedFile}
        onOpenEditModal={handleEditModalOpen}
      />

      <EditFileModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        file={editingFile}
        bp={(isAggregate ? editingFile?.buildingPassport : null) ?? bp}
      />
      <Modal
        open={alreadySharedModalOpen}
        title='Files already shared'
        onClose={() => setAlreadySharedModalOpen(false)}
      >
        <AlreadySharedDialogContent files={alreadySharedFiles} />
      </Modal>
      <CheckFilesModal
        open={checkFilesOpen}
        onClose={() => setCheckFilesOpen(false)}
      />
    </UnifiedFilesWrapper>
  )
}

export default UnifiedFiles

// Styled components (copied from OtherFiles)
const MainContentPanel = styled.div`
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.colors.white};
  min-height: 100px;
  padding: 30px 25px;
  box-shadow: 0 2px 9px rgba(0, 0, 0, 0.03);
  border-radius: 4px;
  flex: 1;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 0;
    padding-bottom: 0;
    min-height: calc(100vh - 120px);
    margin-bottom: 0;
  }
`

const UnifiedFilesWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  position: relative;
`

const PanelHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 30px;
  text-transform: uppercase;
  font-size: 14px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin-bottom: 6px;
    padding: 15px 10px;
    background: ${({ theme }) => theme.colors.white};
  }
`

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 6px !important;
  }
`

const HeaderTotalCount = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
`

const DupToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 0 4px 8px;
`

const BpFilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 0 4px 10px;
`

const BpFilterIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #f1f5f9;
  color: #475569;
  flex-shrink: 0;
`

const BpFilterSelectWrap = styled.div`
  min-width: 260px;
  max-width: 360px;

  @media (max-width: 768px) {
    min-width: 0;
    width: 100%;
  }
`

const BpFilterCount = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: #64748b;
`

const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 12px 0;
  width: 100%;
  min-width: 500px;
  overflow: visible;

  @media (max-width: 768px) {
    min-width: 100%;
    margin: 8px 0;
  }
`

const customBpPulse = keyframes`
  0%, 100% { opacity: 0.5; }
  50%      { opacity: 1; }
`

const CustomBuildingBar = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  max-width: 620px;
  margin: 4px auto 8px;
  padding: 12px 16px;
  background: #f3fbfc;
  border: 1px solid #cdeef1;
  border-radius: 12px;

  @media (max-width: 768px) {
    gap: 10px;
    padding: 10px 12px;
  }
`

const CustomBuildingBarIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #33becc;
  color: #ffffff;
`

const CustomBuildingBarText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
`

const CustomBuildingBarLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.stormGrey || '#6b7280'};
`

const CustomBuildingSelectWrap = styled.div`
  width: 100%;
  max-width: 380px;

  @media (max-width: 768px) {
    max-width: none;
  }
`

const CustomBuildingBarCount = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.stormGrey || '#6b7280'};

  @media (max-width: 768px) {
    display: none;
  }
`

const CustomBpNotice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  max-width: 460px;
  margin: 32px auto;
  padding: 28px 24px;
  text-align: center;
`

const CustomBpNoticeIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: ${({ $forbidden }) => ($forbidden ? '#fff4e5' : '#eef2f7')};
  color: ${({ $forbidden }) => ($forbidden ? '#c2410c' : '#6b7280')};

  svg { font-size: 26px; }
`

const CustomBpNoticeTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti || '#1f2937'};
`

const CustomBpNoticeText = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.stormGrey || '#6b7280'};
`

const CustomBpSkeleton = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  margin-top: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const CustomBpSkeletonRail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 300px;
  flex-shrink: 0;
  padding: 14px;
  border: 1px solid ${({ theme }) => theme.colors.mercury || '#e7e9ee'};
  border-radius: 10px;

  @media (max-width: 768px) {
    width: 100%;
  }
`

const CustomBpSkeletonLine = styled.span`
  height: 14px;
  border-radius: 6px;
  background: #e7e9ee;
  animation: ${customBpPulse} 1.3s ease-in-out infinite;
`

const CustomBpSkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  flex: 1;
  align-content: start;
`

const CustomBpSkeletonCard = styled.div`
  height: 150px;
  border-radius: 10px;
  background: #e7e9ee;
  animation: ${customBpPulse} 1.3s ease-in-out infinite;
`

const PageTitle = styled.h1`
  font-size: 18px !important;
  font-weight: 700 !important;
  margin: 0 !important;
  color: ${({ theme }) => theme.colors.haiti} !important;
  text-transform: uppercase !important;
  letter-spacing: 1px !important;
  line-height: 1.2 !important;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-start;
    padding-right: 136px;
    box-sizing: border-box;
  }
`

const UploadSlot = styled.div`
  @media (max-width: 768px) {
    flex: 1 1 100%;
  }
`

const HeaderBtn = styled.button`
  background-color: #ffffff;
  color: ${({ theme }) => theme.colors.haiti};
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }

  @media (max-width: 768px) {
    min-height: 40px;
    white-space: normal;
    text-align: center;
  }
`

const UncategorisedCounter = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  color: #666;
  white-space: nowrap;

  @media (max-width: 768px) {
    padding: 2px 8px;
    font-size: 10px;
    height: auto;
    min-height: 20px;
    line-height: 1;
    margin-top: 2px;
  }
`

const InfoButton = styled.button`
  @media (max-width: 768px) {
    min-width: 40px;
    min-height: 40px;
  }

  background: none;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 6px;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f3f4f6;
    color: #111827;
    border-color: #d1d5db;
  }
`

const UploadButton = styled.button`
  background-color: ${({ theme }) => theme.colors.secondary};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;

  .desktop-text {
    display: block;
  }
  .mobile-icon {
    display: none;
  }

  @media (max-width: 768px) {
    width: 100%;
    min-height: 44px;
    padding: 0 16px;
    border-radius: 6px;
    gap: 8px;
    font-size: 14px;

    .desktop-text {
      display: block;
    }
    .mobile-icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &:hover {
    background-color: #2aa6b3;
  }
`

const TabsContainer = styled.div`
  margin-bottom: 16px;

  @media (max-width: 768px) {
    margin-bottom: 12px;
    overflow: hidden;
  }
`

const StyledTabs = styled(Tabs)`
  width: 100%;

  .MuiTabs-indicator {
    display: none;
  }

  .MuiTabs-scroller {
    width: 100%;
    overflow: visible !important;
  }

  .MuiTabs-flexContainer {
    gap: 8px;

    @media (max-width: 768px) {
      gap: 5px;
      width: 100%;
      justify-content: space-between;
      padding: 0;
      box-sizing: border-box;
    }
  }
`

const StyledTab = styled(Tab)`
  text-transform: none;
  font-weight: 500;
  color: #000000 !important;
  background-color: white;
  border-radius: 8px !important;
  padding: 10px 24px;
  min-height: 42px;
  transition: all 0.2s ease;
  border: 1px solid #e0e0e0 !important;
  margin: 0 4px;

  @media (max-width: 768px) {
    min-width: 40px !important;
    padding: 0 !important;
    margin: 0 !important;
    height: 46px !important;
    min-height: 46px !important;

    &.Mui-selected {
      flex: 1 1 auto !important;
      max-width: none !important;
    }

    &:not(.Mui-selected) {
      flex: 0 0 48px !important;
      width: 48px !important;
      max-width: 48px !important;
    }

    &:not(.Mui-selected) span {
    }

    .MuiTab-wrapper {
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  &:hover {
    background-color: #f8f8f8;
    border-color: #d0d0d0 !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  &.Mui-selected {
    color: white !important;
    font-weight: 600;
    border: 1px solid transparent !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);

    &:nth-of-type(1) {
      background-color: #33becc;
      border-color: #33becc !important;
    }

    &:nth-of-type(2) {
      background-color: #d24724;
      border-color: #d24724 !important;
    }

    &:nth-of-type(3) {
      background-color: #33becc;
      border-color: #33becc !important;
    }
  }

  .MuiTouchRipple-root {
    border-radius: 8px;
  }
`

const TabContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    gap: ${({ isSelected }) => (isSelected ? '6px' : '0')};
    justify-content: center;
  }

  span {
    font-weight: inherit;
  }
`

const TabText = styled.span`
  @media (max-width: 768px) {
    display: ${({ isSelected }) => (isSelected ? 'inline' : 'none')};
    transition: opacity 0.2s ease;
    opacity: ${({ isSelected }) => (isSelected ? 1 : 0)};
    width: ${({ isSelected }) => (isSelected ? 'auto' : '0')};
    overflow: hidden;
  }
`

const CompanyLogo = styled.img`
  height: 20px;
  width: auto;
  object-fit: contain;

  .Mui-selected & {
    filter: brightness(0) invert(1);
  }
`

const AttentionCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 999px;
  background: #fef3c7;
  color: #b45309;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
`

const Divider = styled.span`
  color: currentColor;
  opacity: 0.5;
  margin: 0 4px;
`

const StyledPersonIcon = styled(PersonIcon)`
  font-size: 20px !important;
  color: inherit;
`

const GoogleDriveLayout = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  @media (max-width: 768px) {
    gap: 0;
    margin-top: 12px;
  }
`

export const TreeNavigationPanel = styled.div`
  width: 260px;
  background: #f8f9fa;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  padding: 2px;
  overflow: hidden;
  height: 100%;
  max-height: 1400px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${({ theme }) => theme.colors.secondary || '#33becc'};
    border-radius: 8px 8px 0 0;
  }

  @media (max-width: 768px) {
    display: none;
  }
`

export const TreeHeader = styled.h3`
  margin: 0 0 6px 0;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  text-transform: uppercase;
  letter-spacing: 1px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  padding: 6px 4px 4px 4px;
  display: flex;
  align-items: center;
`

export const FolderTree = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px;
  min-height: 0;
`

export const TreeFolder = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 4px;
`

export const FolderHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 2px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${({ isSelected, folderColor }) =>
    isSelected ? `${folderColor}15` : 'transparent'};
  border: ${({ isSelected, folderColor }) =>
    isSelected ? `1px solid ${folderColor}` : '1px solid transparent'};
  margin: 1px 0;

  &:hover {
    background-color: ${({ isSelected, folderColor, theme }) =>
      isSelected ? `${folderColor}20` : `${theme.colors.lightGrey}`};
    transform: translateX(1px);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  }

  svg {
    color: ${({ folderColor }) => folderColor};
    font-size: 16px;
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: scale(1.05);
  }
`

export const FolderIconStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1px;
  flex-shrink: 0;

  svg {
    color: ${({ color }) => color};
    font-size: 14px;
    transition: all 0.2s ease;
  }
`

export const FolderNameWithCount = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  flex: 1;
  min-width: 0;
  overflow: hidden;

  > span:first-child {
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.haiti};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
  }
`

export const ExpandIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 2px;
  flex-shrink: 0;

  svg {
    font-size: 14px;
    color: #666;
  }
`

export const CategoryCount = styled.span`
  font-size: 9px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondary || '#33becc'};
  background: ${({ theme }) => theme.colors.secondary || '#33becc'}10;
  padding: 2px 4px;
  border-radius: 3px;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: auto;
`

export const TreeSubFolder = styled.div`
  display: flex;
  align-items: center;
  padding: 2px 2px 2px 14px;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 1px 0;
  background-color: ${({ isSelected, folderColor }) =>
    isSelected ? `${folderColor}15` : 'transparent'};
  border-left: ${({ isSelected, folderColor }) =>
    isSelected ? `2px solid ${folderColor}` : '2px solid transparent'};

  &:hover {
    background-color: ${({ isSelected, folderColor, theme }) =>
      isSelected ? `${folderColor}20` : `${theme.colors.lightGrey}`};
    transform: translateX(1px);
  }
`

export const CategoryIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-right: 1px;
  flex-shrink: 0;

  svg {
    color: ${({ folderColor }) => folderColor};
    font-size: 14px;
    transition: transform 0.2s ease;
  }

  ${TreeSubFolder}:hover & svg {
    transform: scale(1.1);
  }
`

export const CategoryContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  flex: 1;
  min-width: 0;
`

export const CategoryName = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1;
`

export const CategoryFileCount = styled.span`
  font-size: 9px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondary || '#33becc'};
  background: ${({ theme }) => theme.colors.secondary || '#33becc'}10;
  padding: 2px 4px;
  border-radius: 3px;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: auto;
`

const FolderGridPanel = styled.div`
  flex: 1;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  overflow: hidden;
  height: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  position: relative;

  @media (max-width: 768px) {
    border: none;
    border-radius: 0;
    padding: 0 0;
    overflow: visible;
    height: auto;
    min-height: calc(100vh - 200px);
    max-height: none;
    flex: 1 1 auto;
    // padding-bottom: 80px;
    background-color: white;
  }
`

const CustomViewContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  background-color: #f5f5f5;
  border-radius: 8px;
  margin: 20px 0;
`

const CustomViewMessage = styled.p`
  font-size: 16px;
  color: #666;
  text-align: center;
`

const SideBySideContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
`

const SideBySidePanel = styled.div`
  flex: 1;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: white;
  min-height: 500px;
`

const EmptyPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 400px;
`

const EmptyPanelMessage = styled.p`
  color: #999;
  font-size: 14px;
  text-align: center;
`

const TabContentWrapper = styled.div`
  border: 2px solid
  ${({ activeTab }) => {
    switch (activeTab) {
      case 0:
        return '#33becc20'
      case 1:
        return '#D2472420'
      case 2:
        return '#33becc20'
      default:
        return '#e0e0e0'
    }
  }};
  border-radius: 12px;
  padding: 24px;
  margin-top: 24px;
  background-color: ${({ activeTab }) => {
    switch (activeTab) {
      case 0:
        return '#33becc05'
      case 1:
        return '#D2472405'
      case 2:
        return '#33becc05'
      default:
        return '#fafafa'
    }
  }};
  position: relative;
  height: ${({ activeTab }) => (activeTab === 2 ? 'auto' : 'calc(100vh - 220px)')};
  min-height: ${({ activeTab }) => (activeTab === 2 ? '520px' : '1400px')};
  overflow: ${({ activeTab }) => (activeTab === 2 ? 'visible' : 'hidden')};
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    border: none;
    border-radius: 0;
    padding: 8px 4px;
    margin-top: 8px;
    margin-bottom: 0;
    height: auto;
    min-height: 0;
    max-height: none;
    overflow: visible;
    background-color: white;
    flex: 0 0 auto;
  }
`

const TabLabel = styled.div`
  position: absolute;
  top: -14px;
  left: 24px;
  background-color: white;
  padding: 0 12px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ activeTab }) => {
    switch (activeTab) {
      case 0:
        return '#33becc'
      case 1:
        return '#D24724'
      case 2:
        return '#546e7a'
      default:
        return '#666'
    }
  }};
`

const SideBySideLayout = styled.div`
  display: flex;
  gap: 16px;
  height: calc(100vh - 400px);
  min-height: 500px;
`

const SideBySideTreePanel = styled.div`
  width: 280px;
  background: #f8f9fa;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  padding: 2px;
  overflow: hidden;
  height: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  position: relative;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${({ theme }) => theme.colors.secondary || '#33becc'};
    border-radius: 8px 8px 0 0;
  }
`

const SideBySideFilesPanel = styled.div`
  flex: 1;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  overflow: hidden;
  height: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
`

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  gap: 16px;
`

const LoadingText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti || '#333'};
`

const FloatingMenuButton = styled.button`
  display: none;

  @media (max-width: 768px) {
    ${floatingButtonStyle}
    display: inline-flex;
    color: #0c1229;
    position: fixed;
    right: 16px;
    z-index: 900;
    width: auto;
    gap: 6px;
    padding: 0 16px 0 14px;
    border-radius: 999px;

    svg {
      width: 20px;
      height: 20px;
      color: #1f8c97;
    }
  }
`

const FloatingMenuLabel = styled.span`
  font-size: 15px;
  font-weight: 700;
`

const MobileDrawerOverlay = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.2);
    z-index: 1298;
    transition: opacity 0.3s ease;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
  }
`

const MobileDrawer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 50vh;
    max-height: 50vh;
    background: #ffffff;
    box-shadow: 0 5px 25px rgba(0, 0, 0, 0.15);
    z-index: 1299;
    transform: translateY(${({ isOpen }) => (isOpen ? '0' : '-100%')});
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
  }
`

const MobileDrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
  min-height: 56px;
`

const MobileDrawerTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti || '#333'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const MobileDrawerClose = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.haiti || '#333'};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  svg {
    font-size: 24px;
  }
`

const MobileDrawerContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px;
  -webkit-overflow-scrolling: touch;
`
